import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import fields from '../../lib/fields';
import { getHideFields } from '../../lib/editFieldsConfig';
import { loadPublicCalendarData, getCalendarColor } from '../../lib/calendarEventsLoader';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import TranscribeVideoButton from '../admin/transcribe-video-button';
import PollEditor from '../../components/poll/editor';
import ResultsEditor from '../../components/results/editor';
import MapEditor from '../../components/map/editor';
import PostPrompts from './postPrompts';
import ArticleBodyPreview from './bodyPreview';
import ArticleBodyAiTools from './bodyAiTools';
import { isRewriteAction } from '../../lib/articleAssist';
import StlSnapshotter from '../product/stlSnapshotter';
import ColorPicker from '../gui/colorPicker';
import EmojiPicker from '../gui/emojiPicker';
import LazyImage from '../gui/lazyImage';
import { articleBackgroundStyles } from '../../lib/articleBackground';
import { BACKGROUND_PICKER_LIST } from '../../lib/fieldsHelpers';
import {
    BACKGROUND_TEXT_BUDGET,
    BACKGROUND_FONTS,
    BACKGROUND_TEXT_EFFECTS,
    BACKGROUND_TEXT_STYLES,
    BACKGROUND_TEXT_ANIMATIONS,
    BACKGROUND_DECORATIONS,
    BACKGROUND_MOTIONS,
    IMAGE_OVERLAYS,
    IMAGE_FILTERS,
    weightedLength,
    usesPerLineSizes,
    parseBackgroundLines,
    plainBackgroundText,
    reconcileBackgroundText,
    backgroundLineSizeAt,
    setBackgroundLineSize,
    lineFontSize,
    uniformBackgroundFontSize,
    backgroundFontCss,
    backgroundTextEffectKey,
    backgroundTextStyleKey,
    backgroundTextStyleColor,
    backgroundTextStyleOutlineColor,
    backgroundTextAnimationKey,
    backgroundDecorationKey,
    backgroundDecorationSymbols,
    backgroundMotionKey,
    backgroundMotionParticleStyles,
    imageOverlayKey,
    imageOverlayParticles,
    imageFilterKey,
    cleanImageStickerText,
    imageStickerPositionKey,
    IMAGE_STICKER_MAX_LENGTH,
    IMAGE_STICKER_POSITIONS,
    backgroundTextClasses,
    hasPlayableVideo,
} from '../../lib/backgroundPostText';
import MentionTypeahead from '../mentions/typeahead';
import { detectMentionQuery, insertMention, extractValidMentions, MAX_MENTIONS } from '../../lib/mentions';
import CommandTypeahead from '../commands/typeahead';
import { detectCommand, removeCommand } from '../../lib/commands';
import { formatCatchHeadline } from '../../lib/fishCatch';
import { formatContactHeadline } from '../../lib/fishContact';
import { pickOverlayCoordinates } from '../../lib/exifGps';

// Fix default marker icon for Leaflet in bundled environments
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const GENERATED_IMAGE_MAX_ERROR_RETRIES = 60;

// Fields shown when creating a non-feed article (help center, admin lists,
// ticker). Everything else (tags, dates, admin ids) is set afterwards in the
// edit drawer, which keeps the full form. The feed composer (userOnly) has its
// own isSimpleEdit set and is not affected.
const NEW_ARTICLE_FIELDS = ['active', 'title', 'ingress', 'body', 'inlineImages', 'images'];

// Remembered per browser: whether the live body preview sits next to the editor.
const BODY_PREVIEW_PREF_KEY = 'articleBodyPreview';
function readBodyPreviewPref() {
    try {
        return localStorage.getItem(BODY_PREVIEW_PREF_KEY) === '1';
    } catch (e) {
        return false;
    }
}

function LocationPickerEvents({ onLocationSelect }) {
    useMapEvents({
        click(e) {
            onLocationSelect(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
}

@withText(props => ({
    articleNumber: <Text id='input.articleNumber-help'>articleNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    url: <Text id='input.url-help'>url</Text>,
}))
@observer
class NewArticle extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('article'),
            sectionsNormal: fields.get('article', ['Article basics', 'Article meta', 'Article sponsors', 'Article content']),
            sectionsUserOnly: fields.get('article', ['Article simple', 'Article simple video']),
            sectionsUserOnlyMarketplace: fields.get('article', ['Article simple marketplace']),
            showBodyPreview: readBodyPreviewPref(),
            composerModeStarted: false,
            composerActiveSize: null,
            showComposerBackgroundPicker: false,
            showComposerEmojiPicker: false,
            resolvingImageOverlayLocation: false,
            imageOverlayLocationError: false,
            imageOverlayLocationCandidates: [],
            // Image settings (effect, filter, sticker, text-on-image) stay
            // tucked behind the icon on the preview until tapped — Snapchat-style.
            showImageEffectsToolbar: false,
        };
    }

    searchFactboxes = (e) => {
        const term = e.target.value;
        this.setState({ factboxSearch: term });

        if (this.factboxSearchTimer) {
            clearTimeout(this.factboxSearchTimer);
        }

        if (!term || term.length < 2) {
            this.setState({ factboxResults: [], factboxSearching: false });
            return;
        }

        this.setState({ factboxSearching: true });
        this.factboxSearchTimer = setTimeout(async () => {
            const response = await util.fetchApi('/api/articles/', { method: 'GET' }, {
                search: term,
                category: 'factbox',
                limit: 10,
            });
            const results = response?.data || response?.articles || [];
            this.setState({ factboxResults: Array.isArray(results) ? results : [], factboxSearching: false });
        }, 300);
    }

    copyFactboxTag = (articleId) => {
        navigator.clipboard.writeText(`{{factbox.${articleId}}}`);
        this.setState({ copiedFactboxId: articleId });
        setTimeout(() => this.setState({ copiedFactboxId: null }), 2000);
    }

    copyLesogsaTag = (articleId) => {
        navigator.clipboard.writeText(`{{lesogsa.${articleId}}}`);
        this.setState({ copiedLesogsaId: articleId });
        setTimeout(() => this.setState({ copiedLesogsaId: null }), 2000);
    }

    searchProducts = (e) => {
        const term = e.target.value;
        this.setState({ productSearch: term });

        if (this.productSearchTimer) {
            clearTimeout(this.productSearchTimer);
        }

        if (!term || term.length < 2) {
            this.setState({ productSearchResults: [], productSearching: false });
            return;
        }

        this.setState({ productSearching: true });
        this.productSearchTimer = setTimeout(async () => {
            const response = await util.fetchApi('/api/products/', { method: 'GET' }, {
                search: term,
                limit: 10,
            });
            const results = response?.data || [];
            this.setState({ productSearchResults: Array.isArray(results) ? results : [], productSearching: false });
        }, 300);
    }

    copyProductTag = (productId) => {
        navigator.clipboard.writeText(`{{product.${productId}}}`);
        this.setState({ copiedProductId: productId });
        setTimeout(() => this.setState({ copiedProductId: null }), 2000);
    }

    loadEventsCalendars = async () => {
        const { articleStore } = this.props.stores;
        const article = articleStore.article || {};
        if (!article.customer) {
            this.setState({ eventsCalendars: [], eventsCalendarsError: 'Artikkelen mangler kunde.' });
            return;
        }
        this.setState({ eventsCalendarsLoading: true });
        const { calendars, error } = await loadPublicCalendarData(article.customer);
        this.setState({
            eventsCalendars: calendars || [],
            eventsCalendarsError: error || null,
            eventsCalendarsLoading: false,
        });
    }

    copyEventsTag = (calendarId) => {
        const marker = calendarId ? `{{events.${calendarId}}}` : '{{events}}';
        navigator.clipboard.writeText(marker);
        this.setState({ copiedEventsId: calendarId || 'all' });
        setTimeout(() => this.setState({ copiedEventsId: null }), 2000);
    }

    searchLesogsa = (e) => {
        const term = e.target.value;
        this.setState({ lesogsaSearch: term });

        if (this.lesogsaSearchTimer) {
            clearTimeout(this.lesogsaSearchTimer);
        }

        if (!term || term.length < 2) {
            this.setState({ lesogsaResults: [], lesogsaSearching: false });
            return;
        }

        this.setState({ lesogsaSearching: true });
        this.lesogsaSearchTimer = setTimeout(async () => {
            const response = await util.fetchApi('/api/articles/', { method: 'GET' }, {
                search: term,
                limit: 10,
            });
            const results = response?.data || response?.articles || [];
            this.setState({ lesogsaResults: Array.isArray(results) ? results : [], lesogsaSearching: false });
        }, 300);
    }

    createFactbox = async () => {
        const { articleStore, appState } = this.props.stores;
        const { currentCustomer } = appState;
        const title = this.state.newFactboxTitle;
        if (!title?.trim()) return;
        this.setState({ creatingFactbox: true });
        const response = await articleStore.insert({
            title: title.trim(),
            category: 'factbox',
            // A factbox is a fragment whose visibility is governed by the
            // article embedding it — created inactive it would neither be
            // searchable in the Faktaboks tab nor render for readers.
            active: 1,
            customer: currentCustomer.id,
            hostname: articleStore.currentHostname,
        });
        const created = response?.data?.article;
        if (created?.id) {
            this.setState({
                newFactboxTitle: '',
                creatingFactbox: false,
                createdFactboxes: [...(this.state.createdFactboxes || []), created],
            });
            navigator.clipboard.writeText(`{{factbox.${created.id}}}`);
            this.setState({ copiedFactboxId: created.id });
            setTimeout(() => this.setState({ copiedFactboxId: null }), 2000);
        } else {
            this.setState({ creatingFactbox: false });
        }
    }

    editFactbox = (factboxId) => {
        const { appState, articleStore } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMediumLarge } = appState;
        // Opens the dedicated factbox editor (own factboxArticle observable) —
        // a nested editArticle drawer would clobber the articleStore.article
        // singleton this drawer is bound to.
        appState.openDrawer('editFactbox', {
            height: drawerHeightMediumLarge,
            id: factboxId,
            callback: () => {
                articleStore.refreshFactboxCache(factboxId);
            },
        }, drawerLevel + 1);
    }

    toggleBodyPreview = () => {
        this.setState((prev) => {
            const showBodyPreview = !prev.showBodyPreview;
            try {
                localStorage.setItem(BODY_PREVIEW_PREF_KEY, showBodyPreview ? '1' : '0');
            } catch (e) {
                // Preference is a convenience only.
            }
            return { showBodyPreview };
        });
    }

    generateTldr = async () => {
        const { articleStore } = this.props.stores;
        const { article } = articleStore;
        if (!article?.id) return;
        this.setState({ generatingTldr: true });
        await articleStore.generateTldr(article.id);
        this.setState({ generatingTldr: false });
    }

    handleImageMetaChange = (e, imageIndex, field) => {
        const { articleStore } = this.props.stores;
        const { article } = articleStore;
        if (!article?.id || !article.images?.[imageIndex]) return;
        const image = article.images[imageIndex];
        const value = e.target.value;

        // Debounce with 750ms delay
        const timerKey = `img-${imageIndex}-${field}`;
        if (!this.imageMetaTimers) {
            this.imageMetaTimers = {};
        }
        if (this.imageMetaTimers[timerKey]) {
            clearTimeout(this.imageMetaTimers[timerKey]);
        }
        this.imageMetaTimers[timerKey] = setTimeout(() => {
            const params = {
                field: 'images',
                key: field,
                value,
                id: article.id,
                objectName: 'article',
            };
            if (image.md5) {
                params.md5 = image.md5;
            } else if (image.uuidv4) {
                params.uuidv4 = image.uuidv4;
            }
            articleStore.editArrayObject(params);
        }, 750);
    }

    handleFileMetaChange = (e, fileIndex, field) => {
        const { articleStore } = this.props.stores;
        const { article } = articleStore;
        if (!article?.id || !article.files?.[fileIndex]) return;
        const file = article.files[fileIndex];
        const value = e.target.value;

        // Debounce with 750ms delay
        const timerKey = `file-${fileIndex}-${field}`;
        if (!this.fileMetaTimers) {
            this.fileMetaTimers = {};
        }
        if (this.fileMetaTimers[timerKey]) {
            clearTimeout(this.fileMetaTimers[timerKey]);
        }
        this.fileMetaTimers[timerKey] = setTimeout(() => {
            const params = {
                field: 'files',
                key: field,
                value,
                id: article.id,
                objectName: 'article',
            };
            if (file.md5) {
                params.md5 = file.md5;
            } else if (file.uuidv4) {
                params.uuidv4 = file.uuidv4;
            }
            articleStore.editArrayObject(params);
        }, 750);
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id, category, active, userOnly } = props;
        const { articleStore } = this.props.stores;
        if (emptyFirst) {
            articleStore.updateKeyValue('article', {});
            articleStore.updateKeyValue('newArticle', {});
        }
        if (id) {
            const query = {
                hostname: articleStore.currentHostname,
                all: 1,
            };
            if (userOnly) {
                query.userOnly = true;
            }


            await articleStore.load(id, false, { query, addData: ['tags', 'polls'] });
        } else {
            articleStore.updateObjectKeyValue('newArticle', 'category', category);
            articleStore.updateObjectKeyValue('newArticle', 'active', active);
            const { appState } = this.props.stores;
            const { currentCustomer } = appState;
            if (currentCustomer?.id) {
                articleStore.updateObjectKeyValue('newArticle', 'customer', currentCustomer.id);
            }
            const { prefill } = this.props;
            if (prefill && typeof prefill === 'object') {
                Object.keys(prefill).forEach(key => {
                    articleStore.updateObjectKeyValue('newArticle', key, prefill[key]);
                });
            }
        }
    }

    createArticle = async () => {
        const { callback = () => {}, id, isNew, drawerLevel } = this.props;
        const { articleStore, appState } = this.props.stores;
        const { newArticle } = articleStore;

        if (isNew) {
            // Use cached browser geolocation (requested in componentDidMount)
            const browserLocation = {};
            if (this.state.browserLatitude && this.state.browserLongitude) {
                browserLocation.browserLatitude = this.state.browserLatitude;
                browserLocation.browserLongitude = this.state.browserLongitude;
            }

            const response = await articleStore.insert({
                ...newArticle,
                mentions: extractValidMentions(newArticle.ingress || '', newArticle.mentions).slice(0, MAX_MENTIONS),
                ...browserLocation,
                hostname: articleStore.currentHostname,
            });
            const createdArticle = response?.data?.article;
            if (response?.status !== 201 || !createdArticle) {
                // Failed POST (offline, server error): keep the draft and the
                // drawer so the post is never silently lost.
                this.setState({ createArticleError: true });
                return;
            }
            this.setState({ createArticleError: false });
            articleStore.updateKeyValue('article', createdArticle);
            articleStore.updateKeyValue('newArticle', {});

            // Start polling if the article is processing video
            if (createdArticle && createdArticle.videoStatus === 'processing') {
                articleStore.pollVideoStatus(createdArticle.id);
            }

            callback();
            appState.toggleDrawer(false, drawerLevel);
        }
    }

    chooseItem = (e) => {
        const { id } = e.target.closest('.similar-item').dataset;
        const { articleStore } = this.props.stores;
        const { foundList } = articleStore;
        const item = foundList.find((item) => item.id === parseInt(id, 10));
        articleStore.updateKeyValue('newArticle', {
            ...item,
        });
        articleStore.resetSearch();
    }

    toggleSimilar = () => {
        const { showSimilar } = this.state;
        this.setState({
            showSimilar: !showSimilar,
        });
    }

    // ---- @mention typeahead on the ingress textarea (capture phase; input.js is protected) ----

    getIngressTextarea = () => document.querySelector('.article-composer .background-composer-input')
        || document.querySelector('.article-composer textarea[data-field="ingress"]');

    getBackingIngressTextarea = () => Array.from(
        document.querySelectorAll('.article-composer textarea[data-field="ingress"]'),
    ).find(textarea => !textarea.classList.contains('background-composer-input'));

    updateComposerIngress = (value) => {
        const backingTextarea = this.getBackingIngressTextarea();
        if (backingTextarea) {
            this.suppressMentionDetect = true;
            const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
            setter.call(backingTextarea, value);
            backingTextarea.dispatchEvent(new Event('input', { bubbles: true }));
            return;
        }

        // The backing FormFieldsCompact input normally owns persistence. Keep a
        // defensive fallback for the short interval while the form is mounting.
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'ingress', value);
        if (!isNew && id) {
            clearTimeout(this.composerIngressSaveTimer);
            this.composerIngressSaveTimer = setTimeout(() => {
                articleStore.saveField(id, 'ingress', value);
            }, 750);
        }
    }

    handleBackgroundTextInput = (e) => {
        const editArticle = this.getEditArticle();
        let value = reconcileBackgroundText(e.currentTarget.value, editArticle?.ingress || '');
        const activeSize = this.state.composerActiveSize || 'm';
        if (!editArticle?.ingress && value && activeSize !== 'm') {
            value = setBackgroundLineSize(
                value,
                e.currentTarget.selectionStart || 0,
                e.currentTarget.selectionStart || 0,
                activeSize,
            );
        }
        this.updateComposerIngress(value);
        this.setState({
            composerActiveSize: backgroundLineSizeAt(value, e.currentTarget.selectionStart || 0),
        });
    }

    handleBackgroundSelectionChange = (e) => {
        const editArticle = this.getEditArticle();
        this.setState({
            composerActiveSize: backgroundLineSizeAt(
                editArticle?.ingress || '',
                e.currentTarget.selectionStart || 0,
            ),
        });
    }

    toggleComposerBackgroundPicker = () => {
        this.setState({
            showComposerBackgroundPicker: !this.state.showComposerBackgroundPicker,
            showComposerEmojiPicker: false,
        });
    }

    toggleComposerEmojiPicker = () => {
        this.setState({
            showComposerEmojiPicker: !this.state.showComposerEmojiPicker,
            showComposerBackgroundPicker: false,
        });
    }

    setComposerBackground = (value) => {
        const editArticle = this.getEditArticle();
        this.setState({
            composerModeStarted: true,
        });
        this.setBackgroundStyleField('background', value || '');
        if (editArticle?.backgroundImage) {
            this.setBackgroundStyleField('backgroundImage', '');
        }
        if (!value && editArticle?.ingress) {
            this.updateComposerIngress(plainBackgroundText(editArticle.ingress));
        }
    }

    addComposerEmoji = (emoji) => {
        const textarea = this.getIngressTextarea();
        if (!textarea) return;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || start;
        const value = textarea.value || '';
        const nextValue = `${value.slice(0, start)}${emoji}${value.slice(end)}`;
        const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
        setter.call(textarea, nextValue);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.focus();
        const caret = start + emoji.length;
        textarea.setSelectionRange(caret, caret);
    }

    focusBackgroundText = () => {
        this.getIngressTextarea()?.focus();
    }

    handleComposerInputCapture = (e) => {
        if (e.target?.dataset?.field !== 'ingress') return;
        if (this.suppressMentionDetect) {
            this.suppressMentionDetect = false;
            return;
        }
        this.updateMentionState(e.target);
    }

    handleComposerClickCapture = (e) => {
        if (e.target?.dataset?.field !== 'ingress') {
            if (this.state.ingressMention || this.state.ingressCommand) {
                this.setState({ ingressMention: null, ingressCommand: null });
            }
            return;
        }
        this.updateMentionState(e.target);
    }

    handleComposerKeyDownCapture = (e) => {
        const { ingressMention, ingressCommand } = this.state;
        if (!ingressMention && !ingressCommand) return;
        if (e.target?.dataset?.field !== 'ingress') return;
        const ref = ingressCommand ? this.commandTypeaheadRef : this.mentionTypeaheadRef;
        if (ref && ref.handleKey(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    // @mentions remain create-only (matches patch-article.js, which excludes
    // 'mentions' from PATCH array fields) — the isNew guard that used to live in
    // the two capture handlers above now lives here so slash-commands (which DO
    // need to run on already-saved articles, see onFishCatchSave) aren't gated
    // by it too.
    updateMentionState = (textarea) => {
        const text = textarea.value || '';
        const caret = textarea.selectionStart || 0;
        const ingressCommand = detectCommand(text, caret);
        const ingressMention = (this.props.isNew && !ingressCommand)
            ? detectMentionQuery(text, caret) : null;
        // No typeahead open and none opening: no state to change. Bailing out
        // matters beyond perf - see the WebKit note below.
        if (!ingressMention && !ingressCommand
            && !this.state.ingressMention && !this.state.ingressCommand) {
            return;
        }
        let mentionPos = this.state.mentionPos;
        if (ingressMention || ingressCommand) {
            const wrapper = textarea.closest('.article-composer');
            if (wrapper) {
                const tRect = textarea.getBoundingClientRect();
                const wRect = wrapper.getBoundingClientRect();
                mentionPos = `top: ${Math.round(tRect.bottom - wRect.top)}px; left: ${Math.round(tRect.left - wRect.left + 16)}px;`;
            }
        }
        // Deferred past the current event dispatch: this runs from a CAPTURE
        // handler, before input.js's own oninput has written the keystroke to
        // the store. WebKit (Safari/iOS - not Blink/Gecko) flushes Preact's
        // scheduled re-render between the capture and target listeners of a
        // trusted input event, so a synchronous setState here re-rendered the
        // controlled textarea with the store's STALE value and wiped the just-
        // typed character. setTimeout(0) lets the event finish (store updated)
        // before the typeahead state renders.
        if (this.mentionStateTimer) {
            clearTimeout(this.mentionStateTimer);
        }
        this.mentionStateTimer = setTimeout(() => {
            this.mentionStateTimer = null;
            this.setState({ ingressMention, ingressCommand, mentionPos });
        }, 0);
    }

    onComposerMentionSelect = (user) => {
        const { articleStore } = this.props.stores;
        const { newArticle } = articleStore;
        const { ingressMention } = this.state;
        const textarea = this.getIngressTextarea();
        if (!textarea || !ingressMention) return;
        const { newText, newCaret } = insertMention(
            textarea.value, ingressMention.startIndex, textarea.selectionStart, user.name,
        );
        this.suppressMentionDetect = true;
        const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
        setter.call(textarea, newText);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.focus();
        textarea.setSelectionRange(newCaret, newCaret);
        const current = newArticle.mentions || [];
        articleStore.updateObjectKeyValue('newArticle', 'mentions', [...current.filter(m => m.id !== user.id), user]);
        this.setState({ ingressMention: null });
    }

    closeComposerMention = () => {
        this.setState({ ingressMention: null });
    }

    // ---- /commands typeahead on the ingress textarea (same capture-phase mechanism as @mentions above) ----

    closeComposerCommand = () => {
        this.setState({ ingressCommand: null });
    }

    onComposerCommandSelect = (command) => {
        const { ingressCommand } = this.state;
        const textarea = this.getIngressTextarea();
        if (!textarea || !ingressCommand) return;
        const { newText, newCaret } = removeCommand(
            textarea.value, ingressCommand.startIndex, textarea.selectionStart,
        );
        this.suppressMentionDetect = true;
        const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
        setter.call(textarea, newText);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.focus();
        textarea.setSelectionRange(newCaret, newCaret);
        this.setState({ ingressCommand: null });
        if (command.id === 'fishCatch') {
            this.openFishCatchDrawer();
        } else if (command.id === 'fishContact') {
            this.openFishContactDrawer();
        } else if (command.id === 'results') {
            this.openResultsDrawer();
        } else if (command.id === 'map') {
            this.openMapDrawer();
        }
    }

    // ---- Background post styling toolbar (per-line text size, alignment, font) ----

    getEditArticle = () => {
        const { articleStore } = this.props.stores;
        return this.props.isNew ? articleStore.newArticle : articleStore.article;
    }

    // Line sizes are persisted with the legacy marker encoding, but the visual
    // composer applies them without exposing those markers in the text box.
    setComposerLineSize = (e) => {
        const { size } = e.currentTarget.dataset;
        const textarea = this.getIngressTextarea();
        if (!textarea) return;
        const editArticle = this.getEditArticle();
        const selStart = textarea.selectionStart || 0;
        const selEnd = textarea.selectionEnd || selStart;
        const newValue = setBackgroundLineSize(editArticle?.ingress || '', selStart, selEnd, size);
        this.updateComposerIngress(newValue);
        this.setState({ composerActiveSize: size });
        textarea.focus();
        textarea.setSelectionRange(selStart, selEnd);
    }

    // Same dual-mode persistence convention as onFishCatchSave: new posts
    // accumulate on newArticle, saved articles PATCH immediately.
    setBackgroundStyleField = (field, value) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, field, value);
        if (!isNew && id) {
            articleStore.saveField(id, field, value);
        }
    }

    setComposerAlign = (e) => {
        this.setBackgroundStyleField('backgroundAlign', e.currentTarget.dataset.align || 'center');
    }

    setComposerFont = (e) => {
        this.setBackgroundStyleField(
            'backgroundFont',
            e.currentTarget.dataset.font || e.currentTarget.value || '',
        );
    }

    setComposerTextEffect = (e) => {
        this.setBackgroundStyleField(
            'backgroundTextEffect',
            backgroundTextEffectKey(e.currentTarget.value),
        );
    }

    setComposerTextStyle = (e) => {
        this.setBackgroundStyleField(
            'backgroundTextStyle',
            backgroundTextStyleKey(e.currentTarget.value),
        );
    }

    setComposerTextAnimation = (e) => {
        this.setBackgroundStyleField(
            'backgroundTextAnimation',
            backgroundTextAnimationKey(e.currentTarget.value),
        );
    }

    setComposerDecoration = (e) => {
        this.setBackgroundStyleField(
            'backgroundDecoration',
            backgroundDecorationKey(e.currentTarget.value),
        );
    }

    setComposerBackgroundMotion = (e) => {
        this.setBackgroundStyleField(
            'backgroundMotion',
            backgroundMotionKey(e.currentTarget.value),
        );
    }

    setComposerImageOverlay = (e) => {
        const overlay = imageOverlayKey(e.currentTarget.value);
        this.setBackgroundStyleField('imageOverlay', overlay);
        this.setState({ imageOverlayLocationCandidates: [] });
        if (overlay === 'location-sticker') {
            this.resolveComposerImageOverlayLocation();
        }
    }

    getImageOverlayCoordinates = async () => {
        const editArticle = this.getEditArticle();
        const existing = pickOverlayCoordinates({
            image: editArticle?.images?.[0],
            browser: { latitude: this.state.browserLatitude, longitude: this.state.browserLongitude },
            location: editArticle?.location,
        });
        if (existing) {
            return existing;
        }
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            return null;
        }
        return new Promise(resolve => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const coordinates = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    };
                    this.setState({
                        browserLatitude: coordinates.latitude,
                        browserLongitude: coordinates.longitude,
                    });
                    resolve(coordinates);
                },
                () => resolve(null),
                { timeout: 10000, maximumAge: 300000, enableHighAccuracy: false },
            );
        });
    }

    resolveComposerImageOverlayLocation = async () => {
        this.setState({ resolvingImageOverlayLocation: true, imageOverlayLocationError: false });
        const coordinates = await this.getImageOverlayCoordinates();
        if (!coordinates) {
            this.setState({ resolvingImageOverlayLocation: false, imageOverlayLocationError: true });
            return;
        }
        try {
            const response = await util.fetchApi('/api/articles/location-label', { method: 'POST' }, coordinates);
            const label = typeof response?.data?.label === 'string'
                ? response.data.label.trim().slice(0, 80)
                : '';
            if (response?.status < 300 && label) {
                const candidates = Array.isArray(response?.data?.candidates)
                    ? response.data.candidates
                        .map(candidate => (typeof candidate?.label === 'string' ? candidate.label.trim().slice(0, 80) : ''))
                        .filter((candidateLabel, idx, all) => candidateLabel && all.indexOf(candidateLabel) === idx)
                        .slice(0, 4)
                    : [];
                this.setBackgroundStyleField('imageOverlayLocation', label);
                this.setState({
                    resolvingImageOverlayLocation: false,
                    imageOverlayLocationError: false,
                    imageOverlayLocationCandidates: candidates,
                });
                return;
            }
        } catch (err) {
            // The manual label input below remains available when lookup fails.
        }
        this.setState({ resolvingImageOverlayLocation: false, imageOverlayLocationError: true });
    }

    updateComposerImageOverlayLocation = (e) => {
        const { articleStore } = this.props.stores;
        const objectName = this.props.isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'imageOverlayLocation', e.currentTarget.value.slice(0, 80));
    }

    saveComposerImageOverlayLocation = (e) => {
        this.setBackgroundStyleField('imageOverlayLocation', e.currentTarget.value.trim().slice(0, 80));
    }

    chooseComposerImageOverlayLocation = (e) => {
        const label = (e.currentTarget.dataset.label || '').slice(0, 80);
        if (label) {
            this.setBackgroundStyleField('imageOverlayLocation', label);
        }
    }

    setComposerImageFilter = (e) => {
        this.setBackgroundStyleField('imageFilter', imageFilterKey(e.currentTarget.value));
    }

    updateComposerImageStickerText = (e) => {
        const { articleStore } = this.props.stores;
        const objectName = this.props.isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'imageStickerText', e.currentTarget.value.slice(0, IMAGE_STICKER_MAX_LENGTH));
    }

    saveComposerImageStickerText = (e) => {
        this.setBackgroundStyleField('imageStickerText', cleanImageStickerText(e.currentTarget.value));
    }

    setComposerImageStickerPosition = (e) => {
        this.setBackgroundStyleField('imageStickerPosition', imageStickerPositionKey(e.currentTarget.dataset.position));
    }

    toggleImageEffectsToolbar = () => {
        this.setState(prevState => ({ showImageEffectsToolbar: !prevState.showImageEffectsToolbar }));
    }

    toggleComposerTextOverImage = (e) => {
        this.setBackgroundStyleField('textOverImage', !!e.currentTarget.checked);
        this.setState({ composerModeStarted: true });
    }

    enableComposerTextOverImage = () => {
        this.setBackgroundStyleField('textOverImage', true);
        this.setState({ composerModeStarted: true });
    }

    // Mirrors handleBrowseImages in components/form/images.js: open the next
    // free drawer level above whatever is already open (the article editor
    // itself is very likely already showing inside a drawer).
    openFishCatchDrawer = (existingFishCatch = null) => {
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { browserLatitude, browserLongitude } = this.state;
        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('fishCatch', {
            height: drawerHeightMediumLarge,
            fishCatch: existingFishCatch,
            browserLatitude,
            browserLongitude,
            onSave: this.onFishCatchSave,
            onCancel: () => {},
        }, level);
    }

    editFishCatch = (fishCatch) => {
        this.openFishCatchDrawer(fishCatch);
    }

    openBackgroundPresetsDrawer = () => {
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('backgroundPresets', {
            height: drawerHeightMediumLarge,
            onApply: this.applyBackgroundPreset,
        }, level);
    }

    // New posts: fields accumulate on newArticle and go with the create.
    // Saved posts: one PATCH with every preset field (articleStore.save →
    // PATCH /api/articles/:id) instead of seven saveField round trips.
    applyBackgroundPreset = async (fields) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        Object.keys(fields).forEach((field) => {
            articleStore.updateObjectKeyValue(objectName, field, fields[field]);
        });
        this.setState({ composerModeStarted: true, showComposerBackgroundPicker: false });
        if (!isNew && id) {
            await articleStore.save({ ...fields }, id);
        }
    }

    // New (unsaved) posts accumulate the catch locally on newArticle, mirroring
    // how mentions are handled above (onComposerMentionSelect) — createArticle
    // sends the whole newArticle, catches included, on Create. Already-saved
    // articles must persist immediately; 'fishCatches-overwrite' is the same
    // array-replace convention already used for e.g. mapMarkers elsewhere in
    // this file (articleStore.saveField(article.id, 'mapMarkers-overwrite', ...)),
    // backed generically by patch-article.js for every schema field with api:true
    // that is an array type (see backend/lib/class/mongoose/articleSchema.js).
    onFishCatchSave = async (fishCatch) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        const current = articleStore[objectName]?.fishCatches || [];
        const existingIdx = current.findIndex(c => c.uuidv4 === fishCatch.uuidv4);
        const next = existingIdx >= 0
            ? current.map((c, i) => (i === existingIdx ? fishCatch : c))
            : [...current, fishCatch];
        articleStore.updateObjectKeyValue(objectName, 'fishCatches', next);
        if (!isNew && id) {
            await articleStore.saveField(id, 'fishCatches-overwrite', next);
        }
    }

    removeFishCatch = (uuidv4) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        const current = articleStore[objectName]?.fishCatches || [];
        const next = current.filter(c => c.uuidv4 !== uuidv4);
        articleStore.updateObjectKeyValue(objectName, 'fishCatches', next);
        if (!isNew && id) {
            articleStore.saveField(id, 'fishCatches-overwrite', next);
        }
    }

    // /kontakt — a fish that took, followed, was lost or was missed but never
    // landed. Same drawer-level-stacking and dual-mode persistence convention as
    // openFishCatchDrawer/onFishCatchSave above: one chip per contact, editing
    // reopens that single contact.
    openFishContactDrawer = (existingFishContact = null) => {
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { browserLatitude, browserLongitude } = this.state;
        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('fishContact', {
            height: drawerHeightMediumLarge,
            fishContact: existingFishContact,
            browserLatitude,
            browserLongitude,
            onSave: this.onFishContactSave,
            onCancel: () => {},
        }, level);
    }

    editFishContact = (fishContact) => {
        this.openFishContactDrawer(fishContact);
    }

    // New (unsaved) posts accumulate the contact locally on newArticle;
    // already-saved articles persist immediately via 'fishContacts-overwrite',
    // the same array-replace convention backed generically by patch-article.js
    // for every schema field with api:true that is an array type.
    onFishContactSave = async (fishContact) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        const current = articleStore[objectName]?.fishContacts || [];
        const existingIdx = current.findIndex(c => c.uuidv4 === fishContact.uuidv4);
        const next = existingIdx >= 0
            ? current.map((c, i) => (i === existingIdx ? fishContact : c))
            : [...current, fishContact];
        articleStore.updateObjectKeyValue(objectName, 'fishContacts', next);
        if (!isNew && id) {
            await articleStore.saveField(id, 'fishContacts-overwrite', next);
        }
    }

    removeFishContact = (uuidv4) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        const current = articleStore[objectName]?.fishContacts || [];
        const next = current.filter(c => c.uuidv4 !== uuidv4);
        articleStore.updateObjectKeyValue(objectName, 'fishContacts', next);
        if (!isNew && id) {
            articleStore.saveField(id, 'fishContacts-overwrite', next);
        }
    }

    // /resultat — same drawer-level-stacking convention as openFishCatchDrawer.
    // Unlike fishCatch (one chip per catch, editing reopens that single
    // catch), ResultsEditor manages the WHOLE resultsLists collection at
    // once (add/edit/delete lists, CSV import, ...), so there is only ever
    // one drawer instance: it always opens with the full current array, and
    // Lagre always replaces the full array. The podium image picker needs
    // the images of the article actually being composed — newArticle's
    // images when composing a new post, article's when editing a saved one
    // — never the global articleStore.article singleton (see the HARD
    // PREREQUISITE note in components/results/editor.js).
    openResultsDrawer = () => {
        const { appState, articleStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { isNew } = this.props;
        const { newArticle, article } = articleStore;
        const current = isNew ? newArticle : article;
        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('results', {
            height: drawerHeightMediumLarge,
            resultsLists: current?.resultsLists || [],
            images: current?.images || [],
            onSave: this.onResultsSave,
            onCancel: () => {},
        }, level);
    }

    editResultsList = () => {
        this.openResultsDrawer();
    }

    // New (unsaved) posts accumulate on newArticle, mirroring onFishCatchSave;
    // already-saved articles persist immediately via the same
    // '<field>-overwrite' array-replace convention.
    onResultsSave = async (nextResultsLists) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'resultsLists', nextResultsLists);
        if (!isNew && id) {
            await articleStore.saveField(id, 'resultsLists-overwrite', nextResultsLists);
        }
    }

    removeResultsList = (uuidv4) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        const current = articleStore[objectName]?.resultsLists || [];
        const next = current.filter(rl => rl.uuidv4 !== uuidv4);
        articleStore.updateObjectKeyValue(objectName, 'resultsLists', next);
        if (!isNew && id) {
            articleStore.saveField(id, 'resultsLists-overwrite', next);
        }
    }

    // /kart — same drawer-level-stacking convention as openFishCatchDrawer.
    // Cardinality is exactly ONE map per article (many markers, one map), so
    // there is a single drawer instance covering all markers/zoom/center,
    // same reasoning as openResultsDrawer above.
    openMapDrawer = () => {
        const { appState, articleStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { isNew } = this.props;
        const { newArticle, article } = articleStore;
        const current = isNew ? newArticle : article;
        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('map', {
            height: drawerHeightMediumLarge,
            mapMarkers: current?.mapMarkers || [],
            mapZoom: current?.mapZoom != null ? current.mapZoom : 13,
            mapCenter: current?.mapCenter || null,
            onSave: this.onMapSave,
            onCancel: () => {},
        }, level);
    }

    editMap = () => {
        this.openMapDrawer();
    }

    // Three fields, same dual-mode pattern as onFishCatchSave/onResultsSave:
    // mapMarkers via the array-replace 'mapMarkers-overwrite' convention,
    // mapZoom/mapCenter as plain saveField calls (mirroring the existing tab
    // wiring further down in this file). mapZoom is guarded with `!= null`,
    // never truthiness — a zoom of 0 is a real, legitimate value.
    onMapSave = async ({ mapMarkers, mapZoom, mapCenter }) => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'mapMarkers', mapMarkers);
        articleStore.updateObjectKeyValue(objectName, 'mapZoom', mapZoom);
        articleStore.updateObjectKeyValue(objectName, 'mapCenter', mapCenter);
        if (!isNew && id) {
            await articleStore.saveField(id, 'mapMarkers-overwrite', mapMarkers);
            if (mapZoom != null) {
                await articleStore.saveField(id, 'mapZoom', mapZoom);
            }
            if (mapCenter) {
                await articleStore.saveField(id, 'mapCenter', mapCenter);
            }
        }
    }

    // Removing the map chip clears its markers - with none left there is
    // nothing to render, regardless of whatever zoom/center were last set.
    removeMap = () => {
        const { articleStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newArticle' : 'article';
        articleStore.updateObjectKeyValue(objectName, 'mapMarkers', []);
        if (!isNew && id) {
            articleStore.saveField(id, 'mapMarkers-overwrite', []);
        }
    }

    componentDidMount() {
        this.loadAll();
        // Request geolocation early so it's cached by the time user clicks Create
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    this.setState({
                        browserLatitude: pos.coords.latitude,
                        browserLongitude: pos.coords.longitude,
                    });
                },
                () => {}, // silently ignore errors
                { timeout: 10000, maximumAge: 300000, enableHighAccuracy: false },
            );
        }
    }

    componentWillUnmount() {
        if (this.mentionStateTimer) {
            clearTimeout(this.mentionStateTimer);
            this.mentionStateTimer = null;
        }
        if (this.composerIngressSaveTimer) {
            clearTimeout(this.composerIngressSaveTimer);
            this.composerIngressSaveTimer = null;
        }
    }

    openFieldsSettings = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('editFieldsSettings', {
            entityType: 'article',
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    render() {
        const { week, isNew, drawerLevel, installation, lookupArray = 'articles', userOnly, category, ingressPlaceholder, videoMode } = this.props;
        const {
            sections,
            sectionsNormal,
            sectionsUserOnly,
            sectionsUserOnlyMarketplace,
            showSimilar,
            composerModeStarted,
            composerActiveSize,
            showComposerBackgroundPicker,
            showComposerEmojiPicker,
            resolvingImageOverlayLocation,
            imageOverlayLocationError,
            imageOverlayLocationCandidates,
            showImageEffectsToolbar,
        } = this.state;
        const { userStore, articleStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin, customer = {} } = userStore;
        const hideFields = getHideFields(customer, 'article');
        const { newArticle, article, foundList, foundListKeys } = articleStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const fishCatches = (isNew ? newArticle?.fishCatches : article?.fishCatches) || [];
        const fishContacts = (isNew ? newArticle?.fishContacts : article?.fishContacts) || [];
        const resultsLists = (isNew ? newArticle?.resultsLists : article?.resultsLists) || [];
        const mapMarkers = (isNew ? newArticle?.mapMarkers : article?.mapMarkers) || [];

        // Background post styling (per-line sizes, alignment, font, weighted budget).
        const editArticle = isNew ? newArticle : article;

        // Body editor extras: preview toggle + AI tools (full form only, not the feed composer).
        const { showBodyPreview } = this.state;
        const bodyObjectName = isNew ? 'newArticle' : 'article';
        // A rewrite suggestion (korrektur etc.) is shown as a change view in the preview,
        // so the preview opens by itself while such a result is waiting for a decision.
        const { aiAssist = {} } = articleStore;
        const hasRewriteResult = !!(aiAssist.result && isRewriteAction(aiAssist.action));
        const bodyPreviewOpen = showBodyPreview || hasRewriteResult;
        // With the preview open the form leaves its 960px column and uses the whole browser width.
        const bodyPreviewWide = bodyPreviewOpen && !userOnly;
        const bodyFieldToolbar = userOnly ? undefined : {
            body: (
                <div class='d-flex flex-wrap align-items-start gap-1 w-100'>
                    <button
                        type='button'
                        class={`btn btn-sm rounded-pill ${showBodyPreview ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={this.toggleBodyPreview}
                    >
                        <i class={`fa-solid ${showBodyPreview ? 'fa-eye-slash' : 'fa-eye'} me-1`} />
                        <Text id='article.previewToggle'>Preview</Text>
                    </button>
                    <ArticleBodyAiTools ref={c => this.bodyAiTools = c} stores={this.props.stores} objectName={bodyObjectName} />
                </div>
            ),
        };
        const bodyFieldAside = !userOnly && bodyPreviewOpen ? {
            body: (
                <ArticleBodyPreview
                    stores={this.props.stores}
                    objectName={bodyObjectName}
                    onApply={how => this.bodyAiTools && this.bodyAiTools.applyHow(how)}
                />
            ),
        } : undefined;
        const composerBackground = !!(editArticle?.background || editArticle?.backgroundImage);
        const composerIngress = editArticle?.ingress || '';
        const composerWeighted = weightedLength(composerIngress);
        const composerOverBudget = composerWeighted > BACKGROUND_TEXT_BUDGET;
        const composerPerLine = usesPerLineSizes(composerIngress);
        const composerAlign = editArticle?.backgroundAlign === 'left' ? 'left' : 'center';
        const composerFontKey = BACKGROUND_FONTS.some(f => f.key === editArticle?.backgroundFont)
            ? (editArticle?.backgroundFont || '') : '';
        const composerTextEffectKey = backgroundTextEffectKey(editArticle?.backgroundTextEffect);
        const composerTextStyleKey = backgroundTextStyleKey(editArticle?.backgroundTextStyle);
        const composerTextAnimationKey = backgroundTextAnimationKey(editArticle?.backgroundTextAnimation);
        const composerDecorationKey = backgroundDecorationKey(editArticle?.backgroundDecoration);
        const composerDecorationSymbols = backgroundDecorationSymbols(composerDecorationKey);
        const composerBackgroundMotionKey = backgroundMotionKey(editArticle?.backgroundMotion);
        const composerBackgroundMotionParticles = backgroundMotionParticleStyles(composerBackgroundMotionKey);
        const composerHasImage = editArticle?.images?.length > 0;
        const composerImageOverlayKey = imageOverlayKey(editArticle?.imageOverlay);
        const composerImageOverlayParticles = imageOverlayParticles(composerImageOverlayKey);
        const composerImageOverlayLocation = (editArticle?.imageOverlayLocation || '').trim().slice(0, 80);
        const composerImageFilterKey = imageFilterKey(editArticle?.imageFilter);
        const composerImageStickerText = editArticle?.imageStickerText || '';
        const composerImageStickerPosition = imageStickerPositionKey(editArticle?.imageStickerPosition);
        const composerImageStickerShown = cleanImageStickerText(composerImageStickerText);
        // Lights up the collapsed settings icon so a closed panel still tells
        // the user that something non-default is applied to the image.
        const composerImageEffectsActive = composerImageOverlayKey !== 'none'
            || composerImageFilterKey !== 'none'
            || !!composerImageStickerShown
            || !!editArticle?.textOverImage;
        const composerImage = composerHasImage ? editArticle.images[0] : null;
        const composerImagePreviewSrc = composerImage && (
            composerImage.s3XLargeLink
            || composerImage.s3LargeLink
            || composerImage.s3MediumLink
            || composerImage.s3SmallLink
            || composerImage.s3Link
            || ''
        );
        const composerImageFallbackSrc = composerImage && (
            composerImage.lowResBase64
                ? (composerImage.lowResBase64.startsWith('data:')
                    ? composerImage.lowResBase64
                    : `data:image/jpeg;base64,${composerImage.lowResBase64}`)
                : composerImage.s3SmallLink
                    || composerImage.s3ThumbLink
                    || composerImage.s3MediumLink
                    || composerImage.s3Link
                    || ''
        );
        const composerHasVideo = hasPlayableVideo(editArticle);
        const composerHasMedia = composerHasImage || composerHasVideo;
        const composerTextOverImage = !!editArticle?.textOverImage;
        const composerStory = composerTextOverImage && composerHasImage && !composerHasVideo;
        const visualComposer = composerBackground || composerStory || composerModeStarted;
        const showComposerCardBackground = composerBackground && !composerOverBudget && !composerHasMedia;
        const composerPlainText = plainBackgroundText(composerIngress);
        const composerLines = parseBackgroundLines(composerIngress);
        const selectedBackground = editArticle?.backgroundImage || editArticle?.background || '';
        const selectedBackgroundName = BACKGROUND_PICKER_LIST.find(([value]) => value === selectedBackground)?.[1];
        const composerBgStyles = articleBackgroundStyles({
            background: editArticle?.background,
            backgroundImage: editArticle?.backgroundImage,
        });
        const composerTextColor = backgroundTextStyleColor(
            composerTextStyleKey,
            composerBgStyles.textColor,
        );
        const composerTextOutlineColor = backgroundTextStyleOutlineColor(
            composerTextStyleKey,
            composerBgStyles.textOutlineColor,
        );
        const composerCardClass = [
            'background-composer-card',
            ...backgroundTextClasses(editArticle || {}),
        ].filter(Boolean).join(' ');
        const composerFontCss = backgroundFontCss(composerFontKey);
        const composerUniformSize = uniformBackgroundFontSize(composerIngress);
        const composerCurrentSize = composerActiveSize || backgroundLineSizeAt(composerIngress, 0);
        const composerPlaceholder = ingressPlaceholder
            || <Text id='article.backgroundEditorPlaceholder'>Write your post...</Text>;

        //{section.fields && section.fields.filter(field => field.showInList).map((field, idx) => {
        let fields = [];
        let userOnlySections = [];
        if (category === 'marketplace') {
            userOnlySections = userOnly ? sectionsUserOnlyMarketplace : sections;
            const marketplaceFields = sectionsUserOnlyMarketplace.map(section => section.fields.filter(field => field.isMarketPlace)).flat();
            fields.push(...marketplaceFields);
        } else {
            userOnlySections = userOnly ? sectionsUserOnly : sectionsNormal;
            if (userOnly) {
                fields = sectionsUserOnly.map(section => section.fields.filter(field => field.isSimpleEdit)).flat();
            } else if (isNew) {
                // Filtered copies only; the shared section definitions are never mutated.
                fields = sectionsNormal.map(section => section.fields.filter(field => NEW_ARTICLE_FIELDS.includes(field.name))).flat();
            } else {
                fields = sectionsNormal.map(section => section.fields).flat();
            }
        }

        return (<>
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100' style={`max-width: ${bodyPreviewWide ? 'none' : '960px'}; padding-top: 20px; padding-bottom: 100px; ${bodyPreviewWide ? 'padding-left: 16px; padding-right: 16px;' : ''}`}>
                    {/* <div class='w-100 d-flex flex-column justify-content-start'>
                        <xmp>{JSON.stringify(user.settings, null, 2)}</xmp>
                    </div> */}
                    {/* <xmp>{JSON.stringify(newCustomer, null, 4)}</xmp> */}
                    {/* <xmp>{JSON.stringify(installation, null, 4)}</xmp> */}
                    {userOnly ? <></> : <>
                        <div class='font-weight-lighter px-3 box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                            <span>
                                {isNew ? <Text id='article.new'>New article</Text> : <Text id='article.edit'>Edit article</Text>}
                                {installation?.id && <>
                                    <span class='ms-2'>
                                        :: {installation.name}
                                    </span>
                                </>}
                            </span>
                            {/* me-5 keeps the cog clear of the drawer close X when the drawer is no wider than the content column. */}
                            {isCustomerAdmin && <button type='button' class='btn btn-sm btn-outline-secondary rounded-circle me-5'
                                onClick={this.openFieldsSettings}>
                                <i class='fa-solid fa-gear' />
                            </button>}
                        </div>
                    </>}

                    {showSimilar && foundList?.length > 0 && <>
                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                                <div class='mx-4 font-weight-lighter px-3 py-1 text-muted' >
                                    <small class='text-uppercase'>
                                        <Text id='article.similar'>Similar articles</Text>
                                    </small>
                                </div>
                                <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-4 d-flex flex-column px-0 overflow-auto`} style='font-size: 1.1em; border-radius: 10px; max-height: 35vh;'>
                                    {foundList.map((item, idx) => {
                                        return(<>
                                            <div class='d-flex flex-row justify-content-between align-items-center'>
                                                <div
                                                    class='flex-fill similar-item py-2 px-3'
                                                    data-id={item.id}
                                                    onClick={this.chooseItem}
                                                >
                                                    {item.name}
                                                    <span class='font-weight-lighter text-muted ms-3'>{item.articleNumber}</span>
                                                </div>
                                                {/* {item.images[0] && <>
                                                    <div>
                                                        <div class=''>
                                                            <img src={`${item.images[0].s3MediumLink}`} class='rounded-lg img-fluid' style='max-height: 45px;' />
                                                        </div>
                                                    </div>
                                                </>} */}
                                            </div>
                                        </>);
                                    })}
                                </div>
                            </div>
                        </div>
                    </>}

                    <div
                        class={`w-100 d-flex flex-column justify-content-start article-composer position-relative ${visualComposer ? 'article-background-mode' : ''}`}
                        onInputCapture={this.handleComposerInputCapture}
                        onKeyDownCapture={this.handleComposerKeyDownCapture}
                        onClickCapture={this.handleComposerClickCapture}
                    >
                        {/* Shared Input component hardcodes 30x30px thumbnails in the vertical
                            image list — override here so feed image content and order are visible. */}
                        <style>{`
                            .article-composer [data-rbd-droppable-id='droppable-images'] > .flex-row[data-rbd-draggable-id] {
                                height: 80px !important;
                            }
                            .article-composer [data-rbd-droppable-id='droppable-images'] > .flex-row[data-rbd-draggable-id] .border.overflow-hidden.me-2 {
                                width: 72px !important;
                                height: 72px !important;
                                min-width: 72px !important;
                                min-height: 72px !important;
                            }
                            .article-composer [data-rbd-droppable-id='droppable-images'] > .flex-row[data-rbd-draggable-id] .border.overflow-hidden.me-2 > img.img-fluid {
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                            }
                        `}</style>
                        <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                            {isNew && userOnly && category !== 'marketplace' && <>
                                <PostPrompts stores={this.props.stores} />
                            </>}
                            {this.state.ingressMention && (
                                <MentionTypeahead
                                    ref={c => this.mentionTypeaheadRef = c}
                                    stores={this.props.stores}
                                    query={this.state.ingressMention.query}
                                    onSelect={this.onComposerMentionSelect}
                                    onClose={this.closeComposerMention}
                                    posStyle={this.state.mentionPos}
                                />
                            )}
                            {this.state.ingressCommand && (
                                <CommandTypeahead
                                    ref={c => this.commandTypeaheadRef = c}
                                    query={this.state.ingressCommand.query}
                                    onSelect={this.onComposerCommandSelect}
                                    onClose={this.closeComposerCommand}
                                    posStyle={this.state.mentionPos}
                                />
                            )}
                            {/* Image first, text editor after: the composer shell (textarea +
                                text style toolbar) renders BELOW the image preview so the
                                photo stays the hero and its text sits underneath it. */}
                            {composerHasImage && <div class='article-image-effects-panel mx-3 mb-3'>
                                <div class='article-image-effect-preview'>
                                    <div
                                        class={`article-image-preview-media article-image-filter-target article-image-filter-${composerImageFilterKey}`}
                                    >
                                        {composerImageFallbackSrc && <img
                                            class='article-image-preview-placeholder'
                                            src={composerImageFallbackSrc}
                                            alt=''
                                            aria-hidden='true'
                                        />}
                                        <LazyImage
                                            className='article-image-preview-full'
                                            src={composerImagePreviewSrc}
                                            alt={composerImage.alt || composerImage.aiAltText || composerImage.name || ''}
                                            maxErrorRetries={GENERATED_IMAGE_MAX_ERROR_RETRIES}
                                            style='max-height: 320px; object-fit: contain;'
                                        />
                                    </div>
                                    {composerImageOverlayKey === 'location-sticker' ? <div
                                        class='article-image-overlay article-image-overlay-location-sticker'
                                    >
                                        <Localizer>
                                            <div
                                                class='article-image-location-sticker'
                                                aria-label={composerImageOverlayLocation
                                                    ? <Text id='article.imageOverlayLocationAria' fields={{ label: composerImageOverlayLocation }}>Location: {composerImageOverlayLocation}</Text>
                                                    : <Text id='article.imageOverlayLocationFallback'>Location</Text>}
                                            >
                                                <i class='fa-solid fa-location-dot' aria-hidden='true' />
                                                <span>
                                                    {composerImageOverlayLocation
                                                        || <Text id='article.imageOverlayLocationFallback'>Location</Text>}
                                                </span>
                                            </div>
                                        </Localizer>
                                    </div> : composerImageOverlayKey !== 'none' && <div
                                        class={`article-image-overlay article-image-overlay-${composerImageOverlayKey}`}
                                        aria-hidden='true'
                                    >
                                        {composerImageOverlayParticles.map((particle, idx) => (
                                            <span
                                                class='article-image-overlay-particle'
                                                key={`composer-image-overlay-${idx}`}
                                                style={particle.style}
                                            >
                                                {particle.symbol}
                                            </span>
                                        ))}
                                    </div>}
                                    {composerImageStickerShown && <div class={`article-image-sticker article-image-sticker-${composerImageStickerPosition}`}>
                                        <span class='article-image-sticker-text'>{composerImageStickerShown}</span>
                                    </div>}
                                    {composerStory && !composerOverBudget && <div
                                        class={['story-text-layer background-post-display', ...backgroundTextClasses(editArticle || {})].join(' ')}
                                        style={`
                                            ${composerFontCss ? `font-family: ${composerFontCss};` : ''}
                                            color: ${backgroundTextStyleColor(composerTextStyleKey, composerBgStyles.textColor === 'inherit' ? '#ffffff' : composerBgStyles.textColor)};
                                            --background-text-outline: ${composerTextOutlineColor};
                                            text-align: ${composerAlign};
                                        `}
                                    >
                                        <div class='story-text-scrim' aria-hidden='true' />
                                        {composerDecorationSymbols.length > 0 && <div class={`background-decoration-layer background-decoration-${composerDecorationKey}`} aria-hidden='true'>
                                            {composerDecorationSymbols.map((symbol, idx) => <span class='background-decoration-item' key={`story-preview-decoration-${idx}`}>{symbol}</span>)}
                                        </div>}
                                        <div class='story-text-content background-composer-card-content'>
                                            <div class='background-composer-card-text'>
                                                {composerPerLine ? composerLines.map((line, idx) => (
                                                    <div key={`story-line-${idx}`} style={`font-size: ${lineFontSize(line)};`}>{line.text || ' '}</div>
                                                )) : <div style={`font-size: ${composerUniformSize}; white-space: pre-wrap;`}>{composerPlainText}</div>}
                                            </div>
                                        </div>
                                    </div>}
                                    {/* Snapchat-style: the settings live behind this icon on the
                                        photo. Filled when open or when any effect is applied. */}
                                    <Localizer>
                                        <button
                                            type='button'
                                            class={`article-image-effects-toggle btn btn-sm rounded-circle ${showImageEffectsToolbar || composerImageEffectsActive ? 'btn-primary' : 'btn-light'}`}
                                            aria-expanded={showImageEffectsToolbar ? 'true' : 'false'}
                                            aria-controls='articleImageEffectsToolbar'
                                            aria-label={<Text id='article.imageEffectsToggle'>Image settings</Text>}
                                            onClick={this.toggleImageEffectsToolbar}
                                        >
                                            <i class='fa-solid fa-wand-magic-sparkles' aria-hidden='true' />
                                        </button>
                                    </Localizer>
                                </div>
                                {showImageEffectsToolbar && <div class='article-image-effects-toolbar d-flex flex-wrap align-items-center gap-2 p-2' id='articleImageEffectsToolbar'>
                                    <div class='article-image-effect-control d-flex align-items-center gap-2'>
                                        <label class='small text-body-secondary mb-0' for='articleImageOverlay'>
                                            <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                            <Text id='article.imageOverlay'>Image effect</Text>
                                        </label>
                                        <select
                                            id='articleImageOverlay'
                                            class='form-select form-select-sm article-image-overlay-select'
                                            value={composerImageOverlayKey}
                                            onChange={this.setComposerImageOverlay}
                                        >
                                            {IMAGE_OVERLAYS.map(overlay => (
                                                <option key={`image-overlay-${overlay.key}`} value={overlay.key}>
                                                    <Text id={overlay.textKey}>{overlay.label}</Text>
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {composerImageOverlayKey === 'location-sticker' && <div
                                        class='article-image-location-editor d-flex flex-wrap gap-2 w-100'
                                    >
                                        <label class='small text-body-secondary mb-0' for='articleImageOverlayLocation'>
                                            <Text id='article.imageOverlayLocationLabel'>Location sticker text</Text>
                                        </label>
                                        <Localizer>
                                            <input
                                                id='articleImageOverlayLocation'
                                                type='text'
                                                class='form-control form-control-sm article-image-location-input'
                                                value={composerImageOverlayLocation}
                                                maxLength='80'
                                                placeholder={
                                                    <Text id='article.imageOverlayLocationPlaceholder'>Place name</Text>
                                                }
                                                onInput={this.updateComposerImageOverlayLocation}
                                                onBlur={this.saveComposerImageOverlayLocation}
                                            />
                                        </Localizer>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary rounded-pill'
                                            disabled={resolvingImageOverlayLocation}
                                            onClick={this.resolveComposerImageOverlayLocation}
                                        >
                                            <i class={`fa-solid ${resolvingImageOverlayLocation
                                                ? 'fa-spinner fa-spin' : 'fa-location-crosshairs'} me-1`} />
                                            {resolvingImageOverlayLocation
                                                ? <Text id='article.imageOverlayLocationResolving'>
                                                    Finding place...
                                                </Text>
                                                : <Text id='article.imageOverlayLocationResolve'>Use my position</Text>}
                                        </button>
                                        {imageOverlayLocationError && <small class='text-warning w-100'>
                                            <Text id='article.imageOverlayLocationUnavailable'>
                                                Place name could not be found. You can enter it manually.
                                            </Text>
                                        </small>}
                                        {imageOverlayLocationCandidates.length > 0 && <div class='d-flex flex-wrap gap-1 w-100'>
                                            {imageOverlayLocationCandidates.map(candidate => (
                                                <button
                                                    type='button'
                                                    key={`location-candidate-${candidate}`}
                                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                                    data-label={candidate}
                                                    onClick={this.chooseComposerImageOverlayLocation}
                                                >
                                                    {candidate}
                                                </button>
                                            ))}
                                        </div>}
                                    </div>}
                                    <div class='article-image-effect-control d-flex align-items-center gap-2'>
                                        <div class='form-check form-switch mb-0'>
                                            <input
                                                id='articleTextOverImage'
                                                class='form-check-input'
                                                type='checkbox'
                                                role='switch'
                                                checked={composerTextOverImage}
                                                disabled={composerHasVideo}
                                                onChange={this.toggleComposerTextOverImage}
                                            />
                                            <label class='form-check-label small' for='articleTextOverImage'>
                                                <Text id='article.textOverImage'>Text on the image</Text>
                                            </label>
                                        </div>
                                        <small class='text-body-secondary'>
                                            <Text id='article.textOverImageHelp'>Draws your post text over the photo, with the text styles below.</Text>
                                        </small>
                                    </div>
                                    <div class='article-image-effect-control d-flex flex-wrap align-items-center gap-2'>
                                        <label class='small text-body-secondary mb-0' for='articleImageStickerText'>
                                            <i class='fa-solid fa-note-sticky me-1' />
                                            <Text id='article.imageStickerText'>Sticker text</Text>
                                        </label>
                                        <Localizer>
                                            <input
                                                id='articleImageStickerText'
                                                type='text'
                                                class='form-control form-control-sm article-image-sticker-input'
                                                value={composerImageStickerText}
                                                maxLength={IMAGE_STICKER_MAX_LENGTH}
                                                placeholder={<Text id='article.imageStickerPlaceholder'>e.g. HEIA LAGET!</Text>}
                                                onInput={this.updateComposerImageStickerText}
                                                onBlur={this.saveComposerImageStickerText}
                                            />
                                        </Localizer>
                                        <div class='btn-group btn-group-sm' role='group'>
                                            {IMAGE_STICKER_POSITIONS.map(position => (
                                                <button
                                                    type='button'
                                                    key={`sticker-position-${position}`}
                                                    class={`btn ${composerImageStickerPosition === position ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    data-position={position}
                                                    onClick={this.setComposerImageStickerPosition}
                                                >
                                                    {position === 'top'
                                                        ? <Text id='article.imageStickerTop'>Top</Text>
                                                        : <Text id='article.imageStickerBottom'>Bottom</Text>}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div class='article-image-effect-control d-flex align-items-center gap-2'>
                                        <label class='small text-body-secondary mb-0' for='articleImageFilter'>
                                            <i class='fa-solid fa-sliders me-1' />
                                            <Text id='article.imageFilter'>Image filter</Text>
                                        </label>
                                        <select
                                            id='articleImageFilter'
                                            class='form-select form-select-sm article-image-filter-select'
                                            value={composerImageFilterKey}
                                            onChange={this.setComposerImageFilter}
                                        >
                                            {IMAGE_FILTERS.map(filter => (
                                                <option key={`image-filter-${filter.key}`} value={filter.key}>
                                                    <Text id={filter.textKey}>{filter.label}</Text>
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>}
                            </div>}
                            {visualComposer && <div class='background-composer-shell mx-3 mb-3'>
                                {showComposerCardBackground && <div
                                    class={composerCardClass}
                                    onClick={this.focusBackgroundText}
                                    style={`
                                        ${composerBgStyles.backgroundImageCss ? `background-image: ${composerBgStyles.backgroundImageCss};` : ''}
                                        ${composerBgStyles.backgroundColor ? `background-color: ${composerBgStyles.backgroundColor};` : ''}
                                        ${composerFontCss ? `font-family: ${composerFontCss};` : ''}
                                        color: ${composerTextColor || 'inherit'};
                                        --background-text-outline: ${composerTextOutlineColor};
                                        text-align: ${composerAlign};
                                    `}
                                >
                                    {composerBackgroundMotionKey !== 'none' && <div
                                        class={`background-motion-layer background-motion-${composerBackgroundMotionKey}`}
                                        aria-hidden='true'
                                    >
                                        {composerBackgroundMotionParticles.map((particleStyle, idx) => (
                                            <span
                                                class='background-motion-particle'
                                                key={`composer-motion-particle-${idx}`}
                                                style={particleStyle}
                                            />
                                        ))}
                                    </div>}
                                    {composerDecorationSymbols.length > 0 && <div
                                        class={`background-decoration-layer background-decoration-${composerDecorationKey}`}
                                        aria-hidden='true'
                                    >
                                        {composerDecorationSymbols.map((symbol, idx) => (
                                            <span class='background-decoration-item' key={`composer-decoration-${idx}`}>
                                                {symbol}
                                            </span>
                                        ))}
                                    </div>}
                                    {composerPlainText ? <div class='background-composer-card-content'>
                                        {/* Text wrapper: patterned text styles clip their gradient
                                            here, mirroring .feed-post-text in the feed. */}
                                        <div class='background-composer-card-text'>
                                            {composerPerLine ? composerLines.map((line, idx) => (
                                                <div key={`bgline-${idx}`} style={`font-size: ${lineFontSize(line)};`}>
                                                    {line.text || '\u00a0'}
                                                </div>
                                            )) : <div
                                                style={`font-size: ${composerUniformSize}; white-space: pre-wrap;`}
                                            >
                                                {composerPlainText}
                                            </div>}
                                        </div>
                                    </div> : <div class='background-composer-card-placeholder'>{composerPlaceholder}</div>}
                                </div>}

                                {composerBackground && composerHasMedia && !composerStory && <div class='alert alert-secondary py-2 px-3 mb-2 small'>
                                    <i class='fa-solid fa-image me-2' />
                                    <Text id='article.backgroundPausedForMedia'>The background is paused because this post contains an image or video.</Text>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link py-0 px-1 ms-1'
                                        onClick={() => this.setComposerBackground('')}
                                    >
                                        <Text id='article.removeBackground'>Remove background</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link py-0 px-1 ms-1'
                                        onClick={this.enableComposerTextOverImage}
                                    >
                                        <Text id='article.textOverImageFromBackground'>Put the text on the image</Text>
                                    </button>
                                </div>}
                                {(composerBackground || composerStory) && composerOverBudget && <div class='alert alert-danger py-2 px-3 mb-2 small'>
                                    <i class='fa-solid fa-triangle-exclamation me-2' />
                                    <Text id='article.backgroundTooLong'>Too long for a background — make some lines smaller to fit more, or the background will be removed.</Text>
                                </div>}

                                <div class='background-composer-editor'>
                                    <div class='d-flex align-items-center justify-content-between gap-2 px-2 pt-2'>
                                        <label class='small text-body-secondary mb-0' for='backgroundComposerIngress'>
                                            <Text id='article.backgroundEditText'>Text</Text>
                                        </label>
                                        <span class={`small ${composerOverBudget ? 'text-danger fw-bold' : (composerWeighted > BACKGROUND_TEXT_BUDGET * 0.85 ? 'text-warning' : 'text-body-secondary')}`}>
                                            {Math.ceil(composerWeighted)} / {BACKGROUND_TEXT_BUDGET}
                                        </span>
                                    </div>
                                    <Localizer>
                                        <textarea
                                            id='backgroundComposerIngress'
                                            class='background-composer-input'
                                            data-field='ingress'
                                            value={composerPlainText}
                                            placeholder={composerPlaceholder}
                                            onInput={this.handleBackgroundTextInput}
                                            onClick={this.handleBackgroundSelectionChange}
                                            onKeyUp={this.handleBackgroundSelectionChange}
                                            rows='3'
                                        />
                                    </Localizer>
                                </div>

                                <div class='background-composer-toolbar d-flex flex-wrap align-items-center gap-2 p-2'>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary'
                                        onClick={this.openBackgroundPresetsDrawer}
                                    >
                                        <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                        <Text id='article.backgroundPresets'>Style</Text>
                                    </button>
                                    <button
                                        type='button'
                                        class={`btn btn-sm ${showComposerBackgroundPicker ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                        onClick={this.toggleComposerBackgroundPicker}
                                        aria-expanded={showComposerBackgroundPicker ? 'true' : 'false'}
                                    >
                                        <i class='fa-solid fa-palette me-1' />
                                        {selectedBackgroundName || <Text id='article.backgroundPicker'>Background</Text>}
                                    </button>
                                    <button
                                        type='button'
                                        class={`btn btn-sm ${showComposerEmojiPicker ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                        onClick={this.toggleComposerEmojiPicker}
                                        aria-expanded={showComposerEmojiPicker ? 'true' : 'false'}
                                    >
                                        <i class='fa-regular fa-face-smile' />
                                        <span class='visually-hidden'>
                                            <Text id='article.emojiPicker'>Emoji</Text>
                                        </span>
                                    </button>
                                    {(composerBackground || composerStory) && <>
                                        {/* Localizer translates the aria-label <Text> props; the
                                            visible labels are already <Text> children. */}
                                        <Localizer>
                                            <div class='btn-group btn-group-sm' role='group' aria-label={<Text id='article.textSize'>Text size</Text>}>
                                                <button
                                                    type='button'
                                                    class={`btn ${composerCurrentSize === 'l' ? 'btn-secondary' : 'btn-outline-secondary'} fw-bold`}
                                                    data-size='l'
                                                    onClick={this.setComposerLineSize}
                                                >
                                                    <Text id='article.textSizeLarge'>Large</Text>
                                                </button>
                                                <button
                                                    type='button'
                                                    class={`btn ${composerCurrentSize === 'm' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    data-size='m'
                                                    onClick={this.setComposerLineSize}
                                                >
                                                    <Text id='article.textSizeNormal'>Normal</Text>
                                                </button>
                                                <button
                                                    type='button'
                                                    class={`btn ${composerCurrentSize === 's' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    data-size='s'
                                                    onClick={this.setComposerLineSize}
                                                    style='font-size: 0.75em;'
                                                >
                                                    <Text id='article.textSizeSmall'>Small</Text>
                                                </button>
                                            </div>
                                        </Localizer>
                                        <Localizer>
                                            <div class='btn-group btn-group-sm' role='group' aria-label={<Text id='article.textAlignment'>Text alignment</Text>}>
                                                <button
                                                    type='button'
                                                    class={`btn ${composerAlign !== 'left' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    data-align='center'
                                                    onClick={this.setComposerAlign}
                                                >
                                                    <i class='fa-solid fa-align-center' />
                                                </button>
                                                <button
                                                    type='button'
                                                    class={`btn ${composerAlign === 'left' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                    data-align='left'
                                                    onClick={this.setComposerAlign}
                                                >
                                                    <i class='fa-solid fa-align-left' />
                                                </button>
                                            </div>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-font'
                                                value={composerFontKey}
                                                onChange={this.setComposerFont}
                                                aria-label={<Text id='article.backgroundFont'>Font</Text>}
                                                style={composerFontCss ? `font-family: ${composerFontCss};` : ''}
                                            >
                                                {BACKGROUND_FONTS.map(font => (
                                                    <option
                                                        key={`bgfont-${font.key || 'default'}`}
                                                        value={font.key}
                                                        style={font.css ? `font-family: ${font.css};` : ''}
                                                    >
                                                        <Text id={font.textKey}>{font.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-text-style'
                                                value={composerTextStyleKey}
                                                onChange={this.setComposerTextStyle}
                                                aria-label={<Text id='article.backgroundTextStyle'>Text color or pattern</Text>}
                                            >
                                                {BACKGROUND_TEXT_STYLES.map(style => (
                                                    <option key={`bgtextstyle-${style.key}`} value={style.key}>
                                                        <Text id={style.textKey}>{style.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-effect'
                                                value={composerTextEffectKey}
                                                onChange={this.setComposerTextEffect}
                                                aria-label={<Text id='article.backgroundTextEffect'>Text effect</Text>}
                                            >
                                                {BACKGROUND_TEXT_EFFECTS.map(effect => (
                                                    <option key={`bgeffect-${effect.key}`} value={effect.key}>
                                                        <Text id={effect.textKey}>{effect.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-animation'
                                                value={composerTextAnimationKey}
                                                onChange={this.setComposerTextAnimation}
                                                aria-label={<Text id='article.backgroundTextAnimation'>Text animation</Text>}
                                            >
                                                {BACKGROUND_TEXT_ANIMATIONS.map(animation => (
                                                    <option key={`bganimation-${animation.key}`} value={animation.key}>
                                                        <Text id={animation.textKey}>{animation.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-decoration'
                                                value={composerDecorationKey}
                                                onChange={this.setComposerDecoration}
                                                aria-label={<Text id='article.backgroundDecoration'>Decoration</Text>}
                                            >
                                                {BACKGROUND_DECORATIONS.map(decoration => (
                                                    <option key={`bgdecoration-${decoration.key}`} value={decoration.key}>
                                                        <Text id={decoration.textKey}>{decoration.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                        <Localizer>
                                            <select
                                                class='form-select form-select-sm background-composer-motion'
                                                value={composerBackgroundMotionKey}
                                                onChange={this.setComposerBackgroundMotion}
                                                aria-label={<Text id='article.backgroundMotion'>Background motion</Text>}
                                            >
                                                {BACKGROUND_MOTIONS.map(motion => (
                                                    <option key={`bgmotion-${motion.key}`} value={motion.key}>
                                                        <Text id={motion.textKey}>{motion.label}</Text>
                                                    </option>
                                                ))}
                                            </select>
                                        </Localizer>
                                    </>}
                                </div>

                                {showComposerBackgroundPicker && <div class='background-composer-picker border-top p-2'>
                                    <div class='small text-body-secondary mb-2'>
                                        <Text id='article.backgroundPickerChoose'>Choose a background</Text>
                                    </div>
                                    <ColorPicker
                                        options={BACKGROUND_PICKER_LIST}
                                        value={selectedBackground}
                                        onChange={this.setComposerBackground}
                                        allowClear={true}
                                        clearLabel='No background'
                                        label='Choose a post background'
                                        moreLabel={<Text id='article.backgroundPickerShowAll'>Show all</Text>}
                                    />
                                </div>}
                                {showComposerEmojiPicker && <div class='background-composer-emoji border-top'>
                                    <EmojiPicker addEmoji={this.addComposerEmoji} />
                                </div>}
                            </div>}
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={userOnlySections}
                                fields={fields.map(field => field.name)}
                                hideFields={hideFields}
                                holdingStore={articleStore}
                                objectName={isNew ? 'newArticle' : 'article'}
                                isNew={isNew}
                                drawerLevel={drawerLevel}
                                isAdmin={false}
                                lookupArray={lookupArray}
                                rightHeader={isNew && foundList?.length > 0 ? <span class='text-success font-weight-bold'><i class='fa-solid fa-circle-info' /> Similar articles ({foundList.length})</span> : null}
                                rightHeaderClick={this.toggleSimilar}
                                hideFilterToolbar={true}
                                videoMode={videoMode}
                                fieldToolbar={bodyFieldToolbar}
                                fieldAside={bodyFieldAside}
                            />
                            {/* Attachment chip row — one command's attachments per section.
                                Cardinalities differ: many catches, many results lists, but
                                exactly one map (many markers, one attachment), so the map
                                chip is keyed off mapMarkers.length > 0 rather than mapped
                                from an array. */}
                            {(fishCatches.length > 0 || fishContacts.length > 0
                                || resultsLists.length > 0 || mapMarkers.length > 0) && (
                                <div class='d-flex flex-wrap gap-2 px-3 pb-2'>
                                    {fishCatches.map(fishCatch => (
                                        <span
                                            key={fishCatch.uuidv4}
                                            class='badge text-bg-secondary rounded-pill d-flex align-items-center py-2 px-3'
                                        >
                                            <i class='fa-solid fa-fish me-2' />
                                            {formatCatchHeadline(fishCatch) || <Text id='fishCatch.title'>Register catch</Text>}
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Edit catch'
                                                onClick={() => this.editFishCatch(fishCatch)}
                                            >
                                                <i class='fa-solid fa-pen' />
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Remove catch'
                                                onClick={() => this.removeFishCatch(fishCatch.uuidv4)}
                                            >
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </span>
                                    ))}
                                    {fishContacts.map(fishContact => (
                                        <span
                                            key={fishContact.uuidv4}
                                            class='badge text-bg-secondary rounded-pill d-flex align-items-center py-2 px-3'
                                        >
                                            <i class='fa-solid fa-fish-hook me-2' />
                                            {formatContactHeadline(fishContact) || <Text id='fishContact.title'>Register contact</Text>}
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Edit contact'
                                                onClick={() => this.editFishContact(fishContact)}
                                            >
                                                <i class='fa-solid fa-pen' />
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Remove contact'
                                                onClick={() => this.removeFishContact(fishContact.uuidv4)}
                                            >
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </span>
                                    ))}
                                    {resultsLists.map(resultsList => (
                                        <span
                                            key={resultsList.uuidv4}
                                            class='badge text-bg-secondary rounded-pill d-flex align-items-center py-2 px-3'
                                        >
                                            <i class={`${resultsList.icon || 'fa-solid fa-trophy'} me-2`} />
                                            {resultsList.title || <Text id='results.untitled'>Untitled results list</Text>}
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Edit results'
                                                onClick={() => this.editResultsList(resultsList)}
                                            >
                                                <i class='fa-solid fa-pen' />
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Remove results'
                                                onClick={() => this.removeResultsList(resultsList.uuidv4)}
                                            >
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </span>
                                    ))}
                                    {mapMarkers.length > 0 && (
                                        <span class='badge text-bg-secondary rounded-pill d-flex align-items-center py-2 px-3'>
                                            <i class='fa-solid fa-map-location-dot me-2' />
                                            <Text id='map.chip' fields={{ count: mapMarkers.length }}>
                                                {`Map (${mapMarkers.length} markers)`}
                                            </Text>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Edit map'
                                                onClick={this.editMap}
                                            >
                                                <i class='fa-solid fa-pen' />
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body p-0 ms-2'
                                                aria-label='Remove map'
                                                onClick={this.removeMap}
                                            >
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {!isNew && article?.id > 0 && (
                        <StlSnapshotter
                            stores={this.props.stores}
                            object={article}
                            objectName='article'
                        />
                    )}

                    {/* Tabbed tools section (existing articles only) */}
                    {!isNew && article?.id && (() => {
                        const activeTab = this.state.activeEditTab || 'tldr';
                        const hasVideos = article.files?.some(f => f.mimetype?.startsWith('video/'));
                        const hasImages = article.images?.length > 0;

                        const existingFactboxIds = [];
                        const fbRegex = /\{\{factbox\.(\d+)\}\}/g;
                        let fbMatch;
                        while ((fbMatch = fbRegex.exec(article.body || '')) !== null) {
                            existingFactboxIds.push(parseInt(fbMatch[1], 10));
                        }

                        const existingLesogsaIds = [];
                        const laRegex = /\{\{lesogsa\.(\d+)\}\}/g;
                        let laMatch;
                        while ((laMatch = laRegex.exec(article.body || '')) !== null) {
                            existingLesogsaIds.push(parseInt(laMatch[1], 10));
                        }

                        const existingProductIds = [];
                        const prodRegex = /\{\{product\.(\d+)\}\}/g;
                        let prodMatch;
                        while ((prodMatch = prodRegex.exec(article.body || '')) !== null) {
                            existingProductIds.push(parseInt(prodMatch[1], 10));
                        }

                        const tabs = [
                            { id: 'tldr', icon: 'fa-solid fa-robot', label: 'TL;DR' },
                            { id: 'markers', icon: 'fa-solid fa-code', label: 'Markører' },
                            { id: 'images', icon: 'fa-solid fa-image', label: 'Bilder', show: hasImages },
                            { id: 'location', icon: 'fa-solid fa-map-location-dot', label: 'Sted' },
                            { id: 'factbox', icon: 'fa-solid fa-lightbulb', label: 'Faktaboks' },
                            { id: 'lesogsa', icon: 'fa-solid fa-newspaper', label: 'Les også' },
                            { id: 'products', icon: 'fa-solid fa-bag-shopping', label: 'Produkter' },
                            { id: 'map', icon: 'fa-solid fa-map-marked-alt', label: 'Kart' },
                            { id: 'results', icon: 'fa-solid fa-trophy', label: 'Resultater' },
                            { id: 'events', icon: 'fa-solid fa-calendar-days', label: 'Hendelser' },
                            { id: 'poll', icon: 'fa-solid fa-poll', label: 'Avstemning', show: isAdmin },
                            { id: 'video', icon: 'fa-solid fa-video', label: 'Video', show: hasVideos },
                        ].filter(t => t.show !== false);

                        const inlineMarkers = [
                            { marker: '{{factbox.ID}}', icon: 'fa-solid fa-lightbulb', label: 'Faktaboks', desc: 'Setter inn en faktaboks-artikkel. Bytt ut ID med faktaboks-ID. Bruk Faktaboks-fanen for å finne/lage.', copy: false },
                            { marker: '{{lesogsa.ID}}', icon: 'fa-solid fa-newspaper', label: 'Les også', desc: 'Setter inn en "Les også"-boks som lenker til en annen artikkel. Bytt ut ID med artikkel-ID.', copy: false },
                            { marker: '{{product.ID}}', icon: 'fa-solid fa-bag-shopping', label: 'Produkt', desc: 'Setter inn et produktkort. Bytt ut ID med produkt-ID. Bruk Produkter-fanen for å finne.', copy: false },
                            { marker: '{{img.N}}', icon: 'fa-solid fa-image', label: 'Bilde', desc: 'Setter inn et bilde fra artikkelens bildeliste. N er indeks (0, 1, 2, ...). Bruk Bilder-fanen.', copy: false },
                            { marker: '{{video.N}}', icon: 'fa-solid fa-video', label: 'Video', desc: 'Setter inn en video fra artikkelens videoliste. N er video-indeks (0, 1, 2, ...). Bruk Video-fanen.', copy: false },
                            { marker: '{{results.N}}', icon: 'fa-solid fa-trophy', label: 'Resultatliste', desc: 'Setter inn en resultatliste. N er indeks i resultatlistene. Bruk Resultater-fanen.', copy: false },
                            { marker: '{{poll}}', icon: 'fa-solid fa-poll', label: 'Avstemning', desc: 'Setter inn artikkelens avstemning der markøren er plassert.', copy: true },
                            { marker: '{{map}}', icon: 'fa-solid fa-map-marked-alt', label: 'Kart', desc: 'Setter inn kart med artikkelens kartmarkører der markøren er plassert.', copy: true },
                            { marker: '{{events}}', icon: 'fa-solid fa-calendar-days', label: 'Hendelser', desc: 'Setter inn en interaktiv kalender-widget med kommende hendelser og kurs for kunden. Bruk {{events.ID}} eller {{events.ID1,ID2}} for å forhåndsvelge spesifikke kalendere.', copy: true },
                        ];

                        return (
                            <div class='mt-3'>
                                <ul class='nav nav-tabs px-1'>
                                    {tabs.map(tab => (
                                        <li class='nav-item' key={tab.id}>
                                            <button
                                                type='button'
                                                class={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                                onClick={() => {
                                                    this.setState({ activeEditTab: tab.id });
                                                    if (tab.id === 'events' && !this.state.eventsCalendars && !this.state.eventsCalendarsLoading) {
                                                        this.loadEventsCalendars();
                                                    }
                                                }}
                                            >
                                                <i class={`${tab.icon} me-1`} />
                                                <small>{tab.label}</small>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <div class='bg-light p-3'>

                                    {/* TL;DR Tab */}
                                    {activeTab === 'tldr' && (
                                        <div>
                                            {article.tldr && (
                                                <div class='alert alert-light border-start border-4 border-info mb-3'>
                                                    <p class='mb-0'>{article.tldr}</p>
                                                </div>
                                            )}
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary rounded-pill'
                                                onClick={this.generateTldr}
                                                disabled={this.state.generatingTldr}
                                            >
                                                {this.state.generatingTldr ? (
                                                    <><span class='spinner-border spinner-border-sm me-2' /> Genererer...</>
                                                ) : (
                                                    <><i class='fa-solid fa-robot me-2' />{article.tldr ? 'Generer ny TL;DR' : 'Generer TL;DR'}</>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {/* Markers Tab */}
                                    {activeTab === 'markers' && (
                                        <div>
                                            <p class='text-body-secondary small mb-3'>
                                                Skriv en av følgende markører i artikkelteksten der du vil at innholdet skal vises.
                                            </p>
                                            <div class='d-flex flex-column gap-2'>
                                                {inlineMarkers.map(m => (
                                                    <div key={m.marker} class='card'>
                                                        <div class='card-body py-2 px-3 d-flex flex-row align-items-center gap-3'>
                                                            <div class='text-center' style='width: 28px;'>
                                                                <i class={m.icon} />
                                                            </div>
                                                            <div class='flex-fill'>
                                                                <div class='d-flex flex-row align-items-center gap-2'>
                                                                    <strong>{m.label}</strong>
                                                                    <code class='small'>{m.marker}</code>
                                                                </div>
                                                                <div class='text-body-secondary small mt-1'>{m.desc}</div>
                                                            </div>
                                                            {m.copy && (
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                    title={`Kopier ${m.marker} til utklippstavlen`}
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(m.marker);
                                                                        this.setState({ copiedMarker: m.marker });
                                                                        setTimeout(() => this.setState({ copiedMarker: null }), 2000);
                                                                    }}
                                                                >
                                                                    <small>
                                                                        {this.state.copiedMarker === m.marker
                                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                            : <><i class='fa-solid fa-copy me-1' />Kopier</>
                                                                        }
                                                                    </small>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Images Tab */}
                                    {activeTab === 'images' && hasImages && (
                                        <div class='d-flex flex-column gap-3'>
                                            {article.images.map((img, idx) => (
                                                <div key={img.uuidv4 || idx} class='card'>
                                                    <div class='card-body'>
                                                        <div class='d-flex flex-row align-items-start gap-3'>
                                                            <div class='d-flex flex-column align-items-center gap-1'>
                                                                {img.s3SmallLink && (
                                                                    <img
                                                                        src={img.s3SmallLink}
                                                                        alt={img.caption || `Bilde ${idx + 1}`}
                                                                        class='rounded'
                                                                        style='max-height: 80px; max-width: 120px; object-fit: cover;'
                                                                    />
                                                                )}
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                    title={`Kopier {{img.${idx}}} til utklippstavlen`}
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(`{{img.${idx}}}`);
                                                                        this.setState({ copiedImgIdx: idx });
                                                                        setTimeout(() => this.setState({ copiedImgIdx: null }), 2000);
                                                                    }}
                                                                >
                                                                    <small>
                                                                        {this.state.copiedImgIdx === idx
                                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                            : <><i class='fa-solid fa-copy me-1' />{`{{img.${idx}}}`}</>
                                                                        }
                                                                    </small>
                                                                </button>
                                                            </div>
                                                            <div class='flex-fill'>
                                                                <div class='mb-2'>
                                                                    <label class='form-label mb-1 small text-body-secondary'>Bildetekst</label>
                                                                    <input
                                                                        type='text'
                                                                        class='form-control form-control-sm'
                                                                        value={img.caption || ''}
                                                                        placeholder='Skriv bildetekst...'
                                                                        onInput={(e) => this.handleImageMetaChange(e, idx, 'caption')}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label class='form-label mb-1 small text-body-secondary'>Fotograf</label>
                                                                    <input
                                                                        type='text'
                                                                        class='form-control form-control-sm'
                                                                        value={img.photographer || ''}
                                                                        placeholder='Navn på fotograf...'
                                                                        onInput={(e) => this.handleImageMetaChange(e, idx, 'photographer')}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Location Tab */}
                                    {activeTab === 'location' && (() => {
                                        const existingLat = article?.location?.latitude;
                                        const existingLng = article?.location?.longitude;
                                        const lat = this.state.browserLatitude || existingLat;
                                        const lng = this.state.browserLongitude || existingLng;
                                        return (
                                            <div>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                                    onClick={() => this.setState({ showLocationPicker: !this.state.showLocationPicker })}
                                                >
                                                    <i class='fa-solid fa-map-location-dot me-1' />
                                                    {lat ? 'Change location' : 'Set location'}
                                                </button>
                                                {this.state.showLocationPicker && (
                                                    <div class='mt-2' style='height: 50vh; border-radius: 10px; overflow: hidden;'>
                                                        <MapContainer
                                                            center={lat
                                                                ? [lat, lng]
                                                                : [62, 10]}
                                                            zoom={lat ? 10 : 5}
                                                            scrollWheelZoom={true}
                                                            style={{ height: '100%', width: '100%' }}
                                                        >
                                                            <TileLayer
                                                                attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                                                                url='https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png'
                                                            />
                                                            <LocationPickerEvents onLocationSelect={(selectedLat, selectedLng) => {
                                                                this.setState({ browserLatitude: selectedLat, browserLongitude: selectedLng });
                                                                if (article?.id) {
                                                                    articleStore.saveField(article.id, 'location', {
                                                                        latitude: selectedLat,
                                                                        longitude: selectedLng,
                                                                        capturedDate: new Date(),
                                                                    });
                                                                }
                                                            }} />
                                                            {lat && (
                                                                <Marker position={[lat, lng]} />
                                                            )}
                                                        </MapContainer>
                                                    </div>
                                                )}
                                                {lat && (
                                                    <div class='mt-1'>
                                                        <small class='text-body-secondary'>
                                                            {parseFloat(lat).toFixed(4)}, {parseFloat(lng).toFixed(4)}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Faktaboks Tab */}
                                    {activeTab === 'factbox' && (
                                        <div>
                                            {existingFactboxIds.length > 0 && (
                                                <div class='mb-3'>
                                                    <small class='text-body-secondary d-block mb-1'>Brukt i artikkelen:</small>
                                                    <div class='d-flex flex-wrap gap-2'>
                                                        {existingFactboxIds.map(fbId => (
                                                            <span key={`fb-existing-${fbId}`} class='d-inline-flex align-items-center'>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                    onClick={() => this.copyFactboxTag(fbId)}
                                                                >
                                                                    <small>
                                                                        {this.state.copiedFactboxId === fbId
                                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                            : <><i class='fa-solid fa-copy me-1' />{`{{factbox.${fbId}}}`}</>
                                                                        }
                                                                    </small>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-primary px-1 py-0 ms-1'
                                                                    onClick={() => this.editFactbox(fbId)}
                                                                    title='Rediger faktaboks'
                                                                >
                                                                    <small><i class='fa-solid fa-pen' /></small>
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div class='mb-3'>
                                                <small class='text-body-secondary d-block mb-1'>Opprett ny faktaboks:</small>
                                                <div class='d-flex gap-2'>
                                                    <input
                                                        type='text'
                                                        class='form-control form-control-sm'
                                                        placeholder='Tittel på faktaboks...'
                                                        value={this.state.newFactboxTitle || ''}
                                                        onInput={e => this.setState({ newFactboxTitle: e.target.value })}
                                                        onKeyDown={e => { if (e.key === 'Enter') this.createFactbox(); }}
                                                    />
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-primary text-nowrap'
                                                        onClick={this.createFactbox}
                                                        disabled={this.state.creatingFactbox || !this.state.newFactboxTitle?.trim()}
                                                    >
                                                        {this.state.creatingFactbox
                                                            ? <><span class='spinner-border spinner-border-sm me-1' /> Oppretter...</>
                                                            : <><i class='fa-solid fa-plus me-1' />Opprett</>
                                                        }
                                                    </button>
                                                </div>
                                                <small class='text-body-secondary d-block mt-1'>
                                                    Lim inn <code>{`{{factbox.ID}}`}</code> i brødteksten for å vise den.
                                                </small>
                                            </div>

                                            {this.state.createdFactboxes?.length > 0 && (
                                                <div class='mb-3'>
                                                    <small class='text-body-secondary d-block mb-1'>Nylig opprettet:</small>
                                                    <div class='list-group shadow-sm'>
                                                        {this.state.createdFactboxes.map(fb => (
                                                            <div
                                                                key={`fb-created-${fb.id}`}
                                                                class='list-group-item d-flex justify-content-between align-items-center'
                                                            >
                                                                <div>
                                                                    <strong>{fb.title}</strong>
                                                                    <br />
                                                                    <small class='text-body-secondary'>ID: {fb.id}</small>
                                                                </div>
                                                                <div class='d-flex gap-1'>
                                                                    <button
                                                                        type='button'
                                                                        class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                                        onClick={() => this.copyFactboxTag(fb.id)}
                                                                        title='Kopier tag'
                                                                    >
                                                                        <small>
                                                                            {this.state.copiedFactboxId === fb.id
                                                                                ? <span class='text-success'><i class='fa-solid fa-check' /></span>
                                                                                : <i class='fa-solid fa-copy' />
                                                                            }
                                                                        </small>
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        class='btn btn-sm btn-outline-primary px-2 py-0'
                                                                        onClick={() => this.editFactbox(fb.id)}
                                                                        title='Rediger faktaboks'
                                                                    >
                                                                        <small><i class='fa-solid fa-pen' /></small>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <small class='text-body-secondary d-block mb-1'>Søk etter faktaboks:</small>
                                            <input
                                                type='text'
                                                class='form-control form-control-sm mb-2'
                                                placeholder='Søk etter faktaboks...'
                                                value={this.state.factboxSearch || ''}
                                                onInput={this.searchFactboxes}
                                            />

                                            {this.state.factboxSearching && (
                                                <div class='text-body-secondary mb-2'>
                                                    <small><span class='spinner-border spinner-border-sm me-1' /> Søker...</small>
                                                </div>
                                            )}

                                            {this.state.factboxResults?.length > 0 && (
                                                <div class='list-group shadow-sm'>
                                                    {this.state.factboxResults.map(fb => (
                                                        <div
                                                            key={`fb-result-${fb.id}`}
                                                            class='list-group-item d-flex justify-content-between align-items-center'
                                                        >
                                                            <div>
                                                                <strong>{fb.title}</strong>
                                                                <br />
                                                                <small class='text-body-secondary'>ID: {fb.id}</small>
                                                            </div>
                                                            <div class='d-flex gap-1'>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                                    onClick={() => this.copyFactboxTag(fb.id)}
                                                                    title='Kopier tag'
                                                                >
                                                                    <small>
                                                                        {this.state.copiedFactboxId === fb.id
                                                                            ? <span class='text-success'><i class='fa-solid fa-check' /></span>
                                                                            : <i class='fa-solid fa-copy' />
                                                                        }
                                                                    </small>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-primary px-2 py-0'
                                                                    onClick={() => this.editFactbox(fb.id)}
                                                                    title='Rediger faktaboks'
                                                                >
                                                                    <small><i class='fa-solid fa-pen' /></small>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {this.state.factboxSearch?.length >= 2 && !this.state.factboxSearching && this.state.factboxResults?.length === 0 && (
                                                <small class='text-body-secondary'>Ingen faktabokser funnet.</small>
                                            )}
                                        </div>
                                    )}

                                    {/* Les også Tab */}
                                    {activeTab === 'lesogsa' && (
                                        <div>
                                            {existingLesogsaIds.length > 0 && (
                                                <div class='mb-3'>
                                                    <small class='text-body-secondary d-block mb-1'>Brukt i artikkelen:</small>
                                                    <div class='d-flex flex-wrap gap-2'>
                                                        {existingLesogsaIds.map(laId => (
                                                            <span key={`la-existing-${laId}`} class='d-inline-flex align-items-center'>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                    onClick={() => this.copyLesogsaTag(laId)}
                                                                >
                                                                    <small>
                                                                        {this.state.copiedLesogsaId === laId
                                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                            : <><i class='fa-solid fa-copy me-1' />{`{{lesogsa.${laId}}}`}</>
                                                                        }
                                                                    </small>
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <small class='text-body-secondary d-block mb-1'>Søk etter artikkel:</small>
                                            <input
                                                type='text'
                                                class='form-control form-control-sm mb-2'
                                                placeholder='Søk etter artikkel...'
                                                value={this.state.lesogsaSearch || ''}
                                                onInput={this.searchLesogsa}
                                            />

                                            {this.state.lesogsaSearching && (
                                                <div class='text-body-secondary mb-2'>
                                                    <small><span class='spinner-border spinner-border-sm me-1' /> Søker...</small>
                                                </div>
                                            )}

                                            {this.state.lesogsaResults?.length > 0 && (
                                                <div class='list-group shadow-sm'>
                                                    {this.state.lesogsaResults.map(art => (
                                                        <div
                                                            key={`la-result-${art.id}`}
                                                            class='list-group-item d-flex justify-content-between align-items-center'
                                                        >
                                                            <div class='d-flex align-items-center'>
                                                                {art.images?.[0]?.s3SmallLink && (
                                                                    <img
                                                                        src={art.images[0].s3SmallLink}
                                                                        alt=''
                                                                        class='rounded me-2 flex-shrink-0'
                                                                        style='width: 40px; height: 40px; object-fit: cover;'
                                                                    />
                                                                )}
                                                                <div>
                                                                    <strong>{art.title}</strong>
                                                                    <br />
                                                                    <small class='text-body-secondary'>ID: {art.id}</small>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                                onClick={() => this.copyLesogsaTag(art.id)}
                                                                title='Kopier tag'
                                                            >
                                                                <small>
                                                                    {this.state.copiedLesogsaId === art.id
                                                                        ? <span class='text-success'><i class='fa-solid fa-check' /></span>
                                                                        : <i class='fa-solid fa-copy' />
                                                                    }
                                                                </small>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {this.state.lesogsaSearch?.length >= 2 && !this.state.lesogsaSearching && this.state.lesogsaResults?.length === 0 && (
                                                <small class='text-body-secondary'>Ingen artikler funnet.</small>
                                            )}

                                            <small class='text-body-secondary d-block mt-2'>
                                                Lim inn <code>{`{{lesogsa.ID}}`}</code> i brødteksten for å vise en "Les også"-boks.
                                            </small>
                                        </div>
                                    )}

                                    {/* Products Tab */}
                                    {activeTab === 'products' && (
                                        <div>
                                            {existingProductIds.length > 0 && (
                                                <div class='mb-3'>
                                                    <small class='text-body-secondary d-block mb-1'>Brukt i artikkelen:</small>
                                                    <div class='d-flex flex-wrap gap-2'>
                                                        {existingProductIds.map(pId => (
                                                            <span key={`prod-existing-${pId}`} class='d-inline-flex align-items-center'>
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                    onClick={() => this.copyProductTag(pId)}
                                                                >
                                                                    <small>
                                                                        {this.state.copiedProductId === pId
                                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                            : <><i class='fa-solid fa-copy me-1' />{`{{product.${pId}}}`}</>
                                                                        }
                                                                    </small>
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <small class='text-body-secondary d-block mb-1'>Søk etter produkt:</small>
                                            <input
                                                type='text'
                                                class='form-control form-control-sm mb-2'
                                                placeholder='Søk etter produkt...'
                                                value={this.state.productSearch || ''}
                                                onInput={this.searchProducts}
                                            />

                                            {this.state.productSearching && (
                                                <div class='text-body-secondary mb-2'>
                                                    <small><span class='spinner-border spinner-border-sm me-1' /> Søker...</small>
                                                </div>
                                            )}

                                            {this.state.productSearchResults?.length > 0 && (
                                                <div class='list-group shadow-sm'>
                                                    {this.state.productSearchResults.map(prod => (
                                                        <div
                                                            key={`prod-result-${prod.id}`}
                                                            class='list-group-item d-flex justify-content-between align-items-center'
                                                        >
                                                            <div class='d-flex align-items-center'>
                                                                {prod.images?.[0]?.s3SmallLink && (
                                                                    <img
                                                                        src={prod.images[0].s3SmallLink}
                                                                        alt=''
                                                                        class='rounded me-2 flex-shrink-0'
                                                                        style='width: 40px; height: 40px; object-fit: cover;'
                                                                    />
                                                                )}
                                                                <div>
                                                                    <strong>{prod.title || prod.name}</strong>
                                                                    {(prod.price || prod.priceOut) > 0 && (
                                                                        <span class='text-body-secondary ms-2'>
                                                                            Kr. {util.format(prod.price || prod.priceOut, 2)}
                                                                        </span>
                                                                    )}
                                                                    <br />
                                                                    <small class='text-body-secondary'>ID: {prod.id}</small>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                                onClick={() => this.copyProductTag(prod.id)}
                                                                title='Kopier tag'
                                                            >
                                                                <small>
                                                                    {this.state.copiedProductId === prod.id
                                                                        ? <span class='text-success'><i class='fa-solid fa-check' /></span>
                                                                        : <i class='fa-solid fa-copy' />
                                                                    }
                                                                </small>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {this.state.productSearch?.length >= 2 && !this.state.productSearching && this.state.productSearchResults?.length === 0 && (
                                                <small class='text-body-secondary'>Ingen produkter funnet.</small>
                                            )}

                                            <small class='text-body-secondary d-block mt-2'>
                                                Lim inn <code>{`{{product.ID}}`}</code> i brødteksten for å vise produktet inline.
                                            </small>
                                        </div>
                                    )}

                                    {/* Results Tab */}
                                    {activeTab === 'results' && (
                                        <ResultsEditor
                                            resultsLists={this.state.resultsListsLocal || article.resultsLists || []}
                                            images={article.images || []}
                                            onChange={(updated) => {
                                                this.setState({ resultsListsLocal: updated });
                                                articleStore.saveField(article.id, 'resultsLists-overwrite', updated);
                                            }}
                                        />
                                    )}

                                    {/* Poll Tab */}
                                    {activeTab === 'poll' && isAdmin && (
                                        <div>
                                            <PollEditor
                                                stores={this.props.stores}
                                                type='article'
                                                objectId={article.id}
                                                existingPoll={article.polls?.[0]}
                                                onPollCreated={() => {
                                                    this.loadAll(this.props, false);
                                                }}
                                            />
                                            <div class='mt-3'>
                                                <small class='text-body-secondary d-block mb-2'>
                                                    Lim inn <code>{`{{poll}}`}</code> i brødteksten for å bestemme hvor avstemningen vises. Uten markør vises den midt i artikkelen.
                                                </small>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                    onClick={() => {
                                                        navigator.clipboard.writeText('{{poll}}');
                                                        this.setState({ copiedPollTag: true });
                                                        setTimeout(() => this.setState({ copiedPollTag: false }), 2000);
                                                    }}
                                                >
                                                    <small>
                                                        {this.state.copiedPollTag
                                                            ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                            : <><i class='fa-solid fa-copy me-1' />{`{{poll}}`}</>
                                                        }
                                                    </small>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Events Tab */}
                                    {activeTab === 'events' && (() => {
                                        const { eventsCalendars, eventsCalendarsLoading, eventsCalendarsError, copiedEventsId } = this.state;
                                        const eventsRegex = /\{\{events(?:\.([\d,]+))?\}\}/g;
                                        const existingEventsMarkers = [];
                                        let evtMatch;
                                        while ((evtMatch = eventsRegex.exec(article.body || '')) !== null) {
                                            existingEventsMarkers.push(evtMatch[0]);
                                        }
                                        return (
                                            <div>
                                                {existingEventsMarkers.length > 0 && (
                                                    <div class='mb-3'>
                                                        <small class='text-body-secondary d-block mb-1'>Brukt i artikkelen:</small>
                                                        <div class='d-flex flex-wrap gap-2'>
                                                            {existingEventsMarkers.map((m, i) => (
                                                                <span key={`evt-existing-${i}`} class='badge bg-secondary'>{m}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <p class='text-body-secondary small mb-3'>
                                                    Setter inn en interaktiv kalender-widget med kommende hendelser og kurs. Velg alle kalendere eller én spesifikk.
                                                </p>

                                                <div class='card mb-3'>
                                                    <div class='card-body py-2 px-3 d-flex flex-row align-items-center gap-3'>
                                                        <i class='fa-solid fa-calendar-days fa-fw' />
                                                        <div class='flex-fill'>
                                                            <strong>Alle kalendere</strong> <code class='small'>{`{{events}}`}</code>
                                                            <div class='small text-body-secondary'>Viser alle kommende hendelser og kurs for kunden.</div>
                                                        </div>
                                                        <button
                                                            type='button'
                                                            class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                            onClick={() => this.copyEventsTag()}
                                                            title='Kopier'
                                                        >
                                                            <small>
                                                                {copiedEventsId === 'all'
                                                                    ? <span class='text-success'><i class='fa-solid fa-check me-1' />Kopiert</span>
                                                                    : <><i class='fa-solid fa-copy me-1' />Kopier</>
                                                                }
                                                            </small>
                                                        </button>
                                                    </div>
                                                </div>

                                                {eventsCalendarsLoading && (
                                                    <div class='text-body-secondary mb-2'>
                                                        <small><span class='spinner-border spinner-border-sm me-1' /> Laster kalendere...</small>
                                                    </div>
                                                )}

                                                {eventsCalendarsError && (
                                                    <div class='text-danger small mb-2'>{eventsCalendarsError}</div>
                                                )}

                                                {eventsCalendars && eventsCalendars.length > 0 && (
                                                    <>
                                                        <small class='text-body-secondary d-block mb-2'>Eller velg en spesifikk kalender:</small>
                                                        <div class='list-group shadow-sm mb-2'>
                                                            {eventsCalendars.map(cal => {
                                                                const color = getCalendarColor(eventsCalendars, cal.calendarId);
                                                                const isCopied = copiedEventsId === cal.calendarId;
                                                                return (
                                                                    <div
                                                                        key={`evt-cal-${cal.calendarId}`}
                                                                        class='list-group-item d-flex justify-content-between align-items-center'
                                                                    >
                                                                        <div class='d-flex align-items-center gap-2'>
                                                                            <span
                                                                                class='rounded-circle flex-shrink-0'
                                                                                style={`width: 14px; height: 14px; background-color: ${color}; display: inline-block;`}
                                                                            />
                                                                            <div>
                                                                                <strong>{cal.name}</strong>
                                                                                <code class='small ms-2'>{`{{events.${cal.calendarId}}}`}</code>
                                                                                <br />
                                                                                <small class='text-body-secondary'>ID: {cal.calendarId}</small>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            type='button'
                                                                            class='btn btn-sm btn-outline-secondary px-2 py-0'
                                                                            onClick={() => this.copyEventsTag(cal.calendarId)}
                                                                            title='Kopier tag'
                                                                        >
                                                                            <small>
                                                                                {isCopied
                                                                                    ? <span class='text-success'><i class='fa-solid fa-check' /></span>
                                                                                    : <i class='fa-solid fa-copy' />
                                                                                }
                                                                            </small>
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <small class='text-body-secondary d-block'>
                                                            Tips: kombiner flere kalendere ved å skille ID-ene med komma, f.eks. <code>{`{{events.1,2}}`}</code>.
                                                        </small>
                                                    </>
                                                )}

                                                {eventsCalendars && eventsCalendars.length === 0 && !eventsCalendarsLoading && !eventsCalendarsError && (
                                                    <small class='text-body-secondary'>Ingen kalendere funnet for denne kunden.</small>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Map Tab */}
                                    {activeTab === 'map' && (
                                        <MapEditor
                                            stores={this.props.stores}
                                            mapMarkers={this.state.mapMarkersLocal || article.mapMarkers || []}
                                            mapZoom={this.state.mapZoomLocal != null ? this.state.mapZoomLocal : (article.mapZoom != null ? article.mapZoom : 13)}
                                            mapCenter={this.state.mapCenterLocal || article.mapCenter || null}
                                            onChange={(changed) => {
                                                if (changed.mapMarkers) {
                                                    this.setState({ mapMarkersLocal: changed.mapMarkers });
                                                    articleStore.saveField(article.id, 'mapMarkers-overwrite', changed.mapMarkers);
                                                }
                                                if (changed.mapZoom != null) {
                                                    this.setState({ mapZoomLocal: changed.mapZoom });
                                                    articleStore.saveField(article.id, 'mapZoom', changed.mapZoom);
                                                }
                                                if (changed.mapCenter) {
                                                    this.setState({ mapCenterLocal: changed.mapCenter });
                                                    articleStore.saveField(article.id, 'mapCenter', changed.mapCenter);
                                                }
                                            }}
                                        />
                                    )}

                                    {/* Video Tab */}
                                    {activeTab === 'video' && hasVideos && (
                                        <div class='d-flex flex-column gap-3'>
                                            {article.files.map((file, index) => {
                                                if (!file.mimetype || !file.mimetype.startsWith('video/')) {
                                                    return null;
                                                }
                                                // Count video-specific index
                                                let videoIdx = 0;
                                                for (let i = 0; i < index; i++) {
                                                    if (article.files[i]?.mimetype?.startsWith('video/')) {
                                                        videoIdx++;
                                                    }
                                                }
                                                return (
                                                    <div key={`video-${index}`} class='card'>
                                                        <div class='card-body'>
                                                            <div class='row'>
                                                                <div class='col-md-6'>
                                                                    <div class='d-flex justify-content-between align-items-start mb-2'>
                                                                        <h6 class='card-title mb-0'>
                                                                            <i class='fa-solid fa-file-video me-2' />
                                                                            {file.name || `Video ${videoIdx + 1}`}
                                                                        </h6>
                                                                        <button
                                                                            type='button'
                                                                            class='btn btn-sm btn-outline-secondary rounded-pill px-2 py-0'
                                                                            title={`Kopier {{video.${videoIdx}}} til utklippstavlen`}
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(`{{video.${videoIdx}}}`);
                                                                                this.setState({ copiedVideoIdx: videoIdx });
                                                                                setTimeout(() => this.setState({ copiedVideoIdx: null }), 2000);
                                                                            }}
                                                                        >
                                                                            <small>
                                                                                {this.state.copiedVideoIdx === videoIdx
                                                                                    ? <><i class='fa-solid fa-check me-1' />Kopiert</>
                                                                                    : <><i class='fa-solid fa-copy me-1' />{`{{video.${videoIdx}}}`}</>
                                                                                }
                                                                            </small>
                                                                        </button>
                                                                    </div>
                                                                    {file.s3LinkStreamPreview1 && (
                                                                        <img
                                                                            src={file.s3LinkStreamPreview1}
                                                                            alt={file.name}
                                                                            class='img-fluid rounded mb-2'
                                                                            style='max-height: 150px; object-fit: cover;'
                                                                        />
                                                                    )}
                                                                    <p class='card-text mb-1'>
                                                                        <small class='text-muted'>
                                                                            <i class='fa-solid fa-hdd me-1' />
                                                                            {(file.bytes / (1024 * 1024)).toFixed(2)} MB
                                                                        </small>
                                                                    </p>
                                                                    {file.s3Link && (
                                                                        <a
                                                                            href={file.s3Link}
                                                                            target='_blank'
                                                                            rel='noopener noreferrer'
                                                                            class='btn btn-sm btn-outline-secondary rounded-pill mt-2'
                                                                        >
                                                                            <i class='fa-solid fa-external-link me-1' />
                                                                            <Text id='admin.view-video'>View Video</Text>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                <div class='col-md-6'>
                                                                    <TranscribeVideoButton
                                                                        articleId={article.id}
                                                                        fileIndex={index}
                                                                        file={file}
                                                                        language='no'
                                                                        onComplete={() => {
                                                                            this.loadAll(this.props, false);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div class='row mt-3'>
                                                                <div class='col-md-6'>
                                                                    <label class='form-label mb-1 small text-body-secondary'>Bildetekst</label>
                                                                    <input
                                                                        type='text'
                                                                        class='form-control form-control-sm'
                                                                        value={file.caption || ''}
                                                                        placeholder='Skriv bildetekst...'
                                                                        onInput={(e) => this.handleFileMetaChange(e, index, 'caption')}
                                                                    />
                                                                </div>
                                                                <div class='col-md-6'>
                                                                    <label class='form-label mb-1 small text-body-secondary'>Fotograf</label>
                                                                    <input
                                                                        type='text'
                                                                        class='form-control form-control-sm'
                                                                        value={file.photographer || ''}
                                                                        placeholder='Skriv fotograf...'
                                                                        onInput={(e) => this.handleFileMetaChange(e, index, 'photographer')}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                </div>
                            </div>
                        );
                    })()}
                </div>



            </div>

            {isNew && <div class='w-100 d-flex justify-content-center mt-3 px-3 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                <div class='w-100' style='max-width: 960px;'>
                    {this.state.createArticleError && <div class='alert alert-warning py-1 px-2 small text-center mb-2'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <Text id='article.create-failed'>Could not publish. Your draft is kept – check your connection and try again.</Text>
                    </div>}
                    <button type='button' class='btn btn-primary rounded-pill w-100' onClick={this.createArticle}>
                        {userOnly && category !== 'marketplace'
                            ? <Text id='article.publish'>Publish</Text>
                            : <Text id='article.create'>Create article</Text>}
                    </button>
                </div>
            </div>}
        </>);
    }
}

export default NewArticle;
